import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  Signal,
  ViewChild,
  ViewChildren
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
  MatButtonToggleChange,
  MatButtonToggleModule,
} from "@angular/material/button-toggle";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import {
  MatSlideToggleModule,
} from "@angular/material/slide-toggle";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlTree,
} from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { addMinutes, differenceInMilliseconds, differenceInMonths, format } from "date-fns";
import { NoDataComponent } from "projects/ui-library/src/lib/components/no-data/no-data.component";
import { UserAvatarComponent } from "projects/ui-library/src/lib/components/user-avatar/user-avatar.component";
import { HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, filter, finalize, first, firstValueFrom, Observable, Subject, Subscription, takeUntil, throttleTime } from "rxjs";
import { GalleryTool } from "src/app/classes/gallery/gallery.tools";
import { GPSTools } from "src/app/classes/gps/gps.tools";
import { isRisk } from "src/app/classes/risks/risk-group";
import { toError } from "src/app/classes/shared/logs";
import { PDFTool } from "src/app/classes/shared/pdf.tool";
import { Pop } from "src/app/classes/shared/pop";
import { getXYFromLatLonAlt2 } from "src/app/classes/shared/threeDTools";
import { Tools } from "src/app/classes/shared/tools";
import { InviteUserComponent } from "src/app/components/project-pages/stream/dialogs/invite-user/invite-user.component";
import { ALL_ROLES, TagDisplayConfig } from "src/app/components/shared/gallery/gallery.component";
import { LabelerComponent } from "src/app/components/shared/labeler/labeler.component";
import { SearchSelectComponent } from "projects/ui-library/src/lib/components/search-select/search-select.component";
import { VideoCaptureComponent } from "src/app/components/shared/video-capture/video-capture.component";
import { Attachment, CameraType, CameraView, PostAttachment } from "src/app/interfaces/api/attachment";
import { Capture } from "src/app/interfaces/api/capture";
import { Event as _Event, PostEvent } from "src/app/interfaces/api/event";
import { Image as _Image } from "src/app/interfaces/api/image";
import { ImageStatus, StatusLabel } from "src/app/interfaces/api/imageStatus";
import { Label } from "src/app/interfaces/api/label";
import { LabelProject } from "src/app/interfaces/api/labelProject";
import { Risk } from "src/app/interfaces/api/metric";
import { Project } from "src/app/interfaces/api/project";
import { Track } from "src/app/interfaces/api/track";
import { Tracker } from "src/app/interfaces/api/tracker";
import { Unit } from "src/app/interfaces/api/unit";
import { User } from "src/app/interfaces/api/user";
import { Video as _Video } from "src/app/interfaces/api/video";
import { OnLeave } from "src/app/interfaces/shared/guard";
import { SocketUser, StreamBroadcaster, StreamRecording } from "src/app/interfaces/shared/socket";
import { EventService } from "src/app/services/api/events/event.service";
import { ImageService } from "src/app/services/api/images/image.service";
import { MetricService } from "src/app/services/api/metrics/metric.service";
import { ProjectService } from "src/app/services/api/projects/project.service";
import { TrackerService } from "src/app/services/api/trackers/tracker.service";
import { TrackService } from "src/app/services/api/tracks/track.service";
import { UnitService } from "src/app/services/api/units/unit.service";
import { UserService } from "src/app/services/api/users/user.service";
import { AuthService } from "src/app/services/core/auth.service";
import { MediasoupService } from "src/app/services/core/mediasoup/mediasoup.service";
import { WebSocketService } from "src/app/services/core/web-socket.service";
import { ButtonComponent, ConfirmComponent, FileInputComponent, SpinnerComponent } from "ui-library";
import { AttachmentService } from "../../../services/api/attachments/attachment.service";
import { RecordingDone, RecordingProgress, RecordingService } from "../../../services/core/recording.service";
import { WebmStitcher } from "../../../classes/core/webm-stitcher";

interface SavedBroadcaster {
  tracker: string;
  name: string;
  verified?: boolean;
}

interface RecordingContinuation {
  uploadId: string;
  createdDate: string;
  thumbnail: string;
  eventId?: string;
  offset: number;
  fresh: boolean;
}
import { VideoService } from "../../../services/api/videos/video.service";

export interface StreamEvent {
  id: string;
  date: string;
  startDate: Date;
  endDate: Date;
  startDateText: string;
  endDateText: string;
  duration: string;
  images: _Image[];
  risks?: Risk[];
}

interface GalleryInfo {
  index: number;
  canGoLeft: boolean;
  canGoRight: boolean;
}

type CaptureMode = "manual" | "auto";

@Component({
  selector: "app-stream",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    ButtonComponent,
    NoDataComponent,
    // VideoDetectionsComponent,
    UserAvatarComponent,
    MatToolbarModule,
    TranslateModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatMenuTrigger,
    MatButtonToggleModule,
    MatTableModule,
    SpinnerComponent,
    FileInputComponent,
    MatProgressBarModule,
    SearchSelectComponent,
  ],
  templateUrl: "./stream.component.html",
  styleUrls: ["./stream.component.scss"],
})
export class StreamComponent implements OnDestroy, AfterViewInit, OnLeave, OnInit {
  @ViewChild("publisherVideo") publisherVideo!: ElementRef<HTMLVideoElement>;
  trackerVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild("trackerVideo") set trackerVideoRef(el: ElementRef<HTMLVideoElement> | undefined) {
    this.trackerVideo = el;
    if (el) setTimeout(() => this.attachTrackerStream());
  }
  detectionsActive = false;
  hasInitDetections = false;
  @ViewChildren("viewerVideo") viewerVideos!: QueryList<
    ElementRef<HTMLVideoElement>
  >;
  @ViewChild("canvasElement") canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild("rowUpload") rowUpload!: ElementRef<HTMLInputElement>;

  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly userService: UserService = inject(UserService);
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  stream: MediaStream | null = null;
  viewerStream: MediaStream | null = null;
  mediaRecorder: MediaRecorder | null = null;

  isRecording = false;
  isAutoCapturing = false;
  private autoCaptureInterval: any;
  private autoCaptureCreatedDates = new Set<string>();
  private watchdogInterval: any;
  private watchdogLastTime = -1;
  hasMultipleCameras = false;
  videoDevices: MediaDeviceInfo[] = [];
  isSwitchingCamera = false;
  currentFacingMode: "user" | "environment" = "environment";
  viewerFacingMode: "user" | "environment" = "user";
  remoteStream = new MediaStream();

  photos: string[] = [];
  videos: string[] = [];

  recordingTime = "00:00";
  recording: StreamRecording | null = null;
  private static readonly THUMBNAIL_TIMEOUT_MS = 3000;
  private static readonly FILE_THUMBNAIL_TIMEOUT_MS = 10000;
  private static readonly CAPTURE_BATCH_SIZE = 25;
  private static readonly CAPTURE_BATCH_BYTES = 15 * 1024 * 1024;
  private static readonly MAX_PARALLEL_VIDEO_UPLOADS = 1;
  private static readonly MAX_VIDEO_BYTES = 10 * 1024 ** 3;
  // Without a timeslice, ondataavailable only fires at stop.
  private static readonly RECORDER_TIMESLICE_MS = 1000;
  recordedBytes = 0;
  private activeUpload: { uploadId: string; createdDate: string; thumbnail: string; eventId?: string; source?: { id: string; name: string } } | null = null;
  private openingRecorder = false;
  private leavingRecording = false;
  private lastBroadcasterRestore = 0;
  private stateEventId?: string;
  private static readonly BROADCASTER_RESTORE_RETRY_MS = 3000;
  private recorderStopped: Promise<void> = Promise.resolve();
  private recordingInterval: any;
  private recordingAnchor = 0;

  fullscreenMedia: string | null = null;
  fullscreenType: "photo" | "video" | null = null;
  captures: Capture[] = [];
  private fetchedAttachmentIds = new Set<string>();
  width = 720;
  height = 1280;
  maxWidth: number = 0;
  maxHeight: number = 0;
  private userTrackers = new Map<string, Promise<Tracker>>();
  private userUnits = new Map<string, Unit>();
  private gpsTracker: Tracker | null = null;
  user!: User;
  loading = false;
  project!: Project;
  publisher?: SocketUser;
  projectTrackers: Tracker[] = [];
  selectedTrackerName: string | null = null;
  broadcaster: StreamBroadcaster | null = null;
  private activeBroadcaster?: string;
  trackerStreamLive = false;
  trackerNoStream = false;
  private trackerNoStreamTimer?: ReturnType<typeof setTimeout>;
  private static readonly TRACKER_NO_STREAM_MS = 10000;
  viewers: SocketUser[] = [];
  canTakeOver: boolean = false;
  room: string = "";
  isConnected: boolean = false;
  users: SocketUser[] = [];
  destroy$ = new Subject<void>();
  gpsDestroy$!: Subject<void>; //= new Subject<void>();
  private hasLeft = false;
  mapDisplayed = false;
  aspect: string = "";
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      risk: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      crane: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] },
      zone: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] },
      action: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] }
    }
  };
  showGallery: boolean = false;
  socketImageSub!: Subscription;
  metricSub?: Subscription;
  imageStatusSub?: Subscription;
  imageSyncSub?: Subscription;
  videoSyncSub?: Subscription;
  private streamEventSub?: Subscription;
  // initSockets() runs on EVERY socket (re)connect, so this must be unsubscribed
  // before re-subscribing — otherwise mediasoup.status handlers pile up and every
  // status.emit is processed N times (duplicate consumers, wasted work, leak).
  private statusSub?: Subscription;
  labelerDialogRef?: MatDialogRef<LabelerComponent>;
  private videoCaptureDialogRef?: MatDialogRef<VideoCaptureComponent>;
  isMobile: boolean = false;
  publisherParams: string[] = ["camera", "mic"];
  viewerParams: string[] = ["camera", "mic"];
  labelProject?: LabelProject;
  labels: Label[] = [];
  labelProjects: LabelProject[] = [];
  private imageRands = new Map<string, string>();
  events: _Event[] = [];
  eventsData: MatTableDataSource<_Event> = new MatTableDataSource();
  eventColumns: string[] = ["date", "images", "status"];
  loadingEvents: boolean = false;
  loadingReport: Record<string, boolean> = {};
  risks: Risk[] = [];
  galleryInfo: GalleryInfo = { index: -1, canGoLeft: true, canGoRight: true };
  isLandscape: boolean = false;
  // Peers whose media transport is mid-recovery — used to show a loader and freeze the
  // aspect ratio so the video box doesn't flash to square during the reconnect gap.
  reconnectingPeers = new Set<string>();
  private aspectSnapshot?: string;
  // connectStream() en vol : voir initMediaSoup().
  private mediasoupInit: Promise<void> | null = null;
  attachmentIds: string[] = [];
  producerReady: boolean = false;
  event?: _Event;
  allInvited: SocketUser[] = [];
  invited: SocketUser[] = [];
  startDate: string = new Date().toISOString();
  eventStates: Map<string | undefined, { publisher: SocketUser | undefined, viewers: SocketUser[] }> = new Map();
  eventId: string = "";
  joiningRoom: boolean = false;
  // Sidebar de droite (rejoindre / déposer) déclenchée par « Créer une visite HSE ».
  showVisitSidebar: boolean = false;
  // 'create' = créer/rejoindre une visite ; 'detail' = aperçu d'une visite (clic ligne).
  autoplayBlocked = new Set<string>();
  private isReconnecting = false;
  private static readonly DISCONNECT_GRACE_MS = 8000;
  private disconnectRecoveryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly CONSUMER_CHECK_MS = 3000;
  private static readonly CONSUMER_STALL_CHECKS = 3;
  private static readonly CONSUMER_MAX_RECOVERIES = 3;
  private consumerWatchdogInterval: any = null;
  private consumerStallCounts = new Map<string, number>();
  private consumerLastProgress = new Map<string, number>();
  private consumerRecoveries = 0;
  private readonly pendingVideos = new Map<string, {
    url: string;
    thumbnail: string;
    eventId: string | undefined;
    file: File;
    placeholder: Capture;
    createdDate: string;
    segment: boolean;
    tracker?: { id: string; name: string };
    state$: BehaviorSubject<{ uploading: boolean; videoSrc?: string } | null>;
    dialogOpen?: boolean;
    id?: string;
    attachmentId?: string;
    recording?: boolean;
  }>();
  processingVideos = 0;
  private activeVideoUploads = 0;
  private videoUploadQueue: string[] = [];
  private static readonly CAPTURE_REQUEST_TIMEOUT_MS = 60000;
  private static readonly MAX_CAPTURE_EDGE = 4096;
  private static readonly VIDEO_UPLOAD_HEARTBEAT_MS = 20000;
  private static readonly REMOTE_VIDEO_UPLOAD_STALE_MS = 60000;
  private videoUploadHeartbeat: ReturnType<typeof setInterval> | null = null;
  private videoUploadLastBroadcast = new Map<string, { at: number; stage?: number; progress?: number }>();
  private readonly remoteVideoUploads = new Map<string, { placeholder: Capture; eventId: string; staleTimer?: ReturnType<typeof setTimeout> }>();

  constructor(
    private attachmentService: AttachmentService,
    private videoService: VideoService,
    private trackerService: TrackerService,
    private unitService: UnitService,
    private eventService: EventService,
    private auth: AuthService,
    private pop: Pop,
    private mediasoup: MediasoupService,
    private ws: WebSocketService,
    private router: Router,
    private gps: GPSTools,
    private trackService: TrackService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private imageService: ImageService,
    private galleryTool: GalleryTool,
    private metricService: MetricService,
    private pdfTool: PDFTool,
    private route: ActivatedRoute,
    private tools: Tools,
    private recordingService: RecordingService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects.includes("stream")) {
          this.hasLeft = false;
        }
      }
    });
    effect(() => {
      const project = this.currentProject();
      if (this.project && project?.id && this.project.id !== project.id) {
        this.events = [];
        this.eventsData.data = [];
        this.captures = [];
        this.fetchedAttachmentIds.clear();
        this.risks = [];
        this.attachmentIds = [];
        this.event = undefined;
        this.userTrackers.clear();
        this.userUnits.clear();
        this.gpsTracker = null;
        this.ngOnDestroy();
        this.destroy$ = new Subject<void>();
        this.ngAfterViewInit();
        this.getStreamEvents();
      }
    });
    this.galleryTool
      .getLabelData("risk")
      .then(async ({ labelProject, labels }) => {
        const { labelProject: workerProject, labels: workerLabels } = await this.galleryTool.getLabelData("worker");
        const faceLabel: Label | undefined = workerProject && (workerLabels.find(l => l.name.toLowerCase() === "face")
          ?? { id: "worker-face", project: workerProject, module: workerProject.name, name: "Face", displayName: "Face", type: "tag", color: "#0066ff", createdDate: new Date() });
        this.labelProject = labelProject;
        this.labelProjects = [labelProject, workerProject].filter((lp): lp is LabelProject => !!lp);
        const riskLabels = labels.map(l => ({ ...l, module: labelProject?.name }));
        this.labels = faceLabel ? [{ ...faceLabel, module: workerProject!.name }, ...riskLabels] : riskLabels;
      });
  }

  async ngOnInit() {
    this.isMobile = window.innerWidth < 768;
    const queryParams = this.route.snapshot.queryParamMap;
    const projectId = queryParams.get("project");
    const eventId = queryParams.get("event");
    const mode = queryParams.get("mode");
    if (mode === 'user' || mode === 'environment') {
      this.currentFacingMode = mode;
    }

    if (eventId) {
      this.eventId = eventId;
      this.ws.setEvent(eventId);
      if (queryParams.get("joined") === "true") {
        this.joiningRoom = true;
      } else {
        this.showVisitSidebar = true;
      }
    }

    if (projectId) {
      this.projectService
        .getProjectById(projectId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (project) => {
          this.project = project;
          this.projectService.setCurrentProject(project);
          this.projectService.getStatus(project.database).pipe(takeUntil(this.destroy$)).subscribe((result) => {
            this.project.status = result[0];
            this.projectService.setCurrentProject(project);
          });

          if (eventId) {
            this.getStreamEvents([eventId]);
          } else {
            this.getStreamEvents();
          }
        },
          (err) => {
            console.error("Error loading project:", err);
          },
        );
    } else {
      this.getStreamEvents();
      // effect(() => {
      //   this.project = this.currentProject();
      //   console.log("EFFECT, this.project :>> ", this.project);
      //   if (eventId && startDateParam) {
      //     this.loadEvent(eventId);
      //   }
      // });
    }

  }

  async ngAfterViewInit() {
    this.ws.reconnectWithCurrentUser();
    this.initAllSockets();
    // await this.initMediaSoup();
    // this.initSockets();
    this.hasLeft = false;
    this.aspect = this.getOrientation();
    // this.getImages();
    this.initRecordingUploads();
  }

  private initRecordingUploads() {
    this.recordingService.progress$.pipe(takeUntil(this.destroy$)).subscribe((p) => this.onRecordingProgress(p));
    this.recordingService.done$.pipe(takeUntil(this.destroy$)).subscribe((d) => this.onRecordingDone(d));
    this.recordingService.dropped$.pipe(takeUntil(this.destroy$)).subscribe((d) => this.onRecordingDropped(d));
    this.recordingService.full$.pipe(takeUntil(this.destroy$)).subscribe((id) => this.onRecordingFull(id));
    this.recordingService.lowSpace$.pipe(takeUntil(this.destroy$), throttleTime(60000)).subscribe(() =>
      this.pop.warning(this.translate.instant("stream.recording_low_space")));
    const email = this.auth.currentUser()?.email;
    if (email) void this.recordingService.resume(email);
  }

  ngOnDestroy() {
    this.metricSub?.unsubscribe();
    this.imageStatusSub?.unsubscribe();
    this.imageSyncSub?.unsubscribe();
    this.videoSyncSub?.unsubscribe();
    this.streamEventSub?.unsubscribe();
    this.statusSub?.unsubscribe();
    this.clearDisconnectRecovery();
    this.stopConsumerWatchdog();
    this.leaveRoom();
    this.destroy$.next();
    this.destroy$.complete();
    // After destroy$: closing the peer emits 'peer-closed', and a live
    // subscription would rebuild one for a component that is going away.
    // Here only, not in leaveRoom(): quitting keeps the component mounted.
    if (this.user?.email) this.mediasoup.cleanupPeer(this.user.email);
    if (this.gpsDestroy$) {
      this.gpsDestroy$.next();
      this.gpsDestroy$.complete();
      this.gps.destroy();
    }
    this.videoUploadQueue = [];
    this.pendingVideos.forEach(pending => URL.revokeObjectURL(pending.url));
    this.pendingVideos.clear();
    this.stopVideoUploadHeartbeat();
    this.remoteVideoUploads.forEach(remote => clearTimeout(remote.staleTimer));
    this.remoteVideoUploads.clear();
  }

  // ---------- INIT FUNCTIONS -----------------------------------------------------------
  async initMediaSoup() {
    // Single-flight: the peerId is the email, so two calls give the SFU a
    // duplicate and it evicts the first, which starts the recoverPeer loop.
    // isPeerReady() alone is not enough -- it stays false during the first call.
    if (this.mediasoup.isPeerReady(this.user.email) && this.mediasoup.room === this.room) {
      this.startConsumerWatchdog();
      return;
    }
    if (this.mediasoupInit) return this.mediasoupInit;

    this.mediasoupInit = this.mediasoup
      .connectStream(this.user.email, this.room, true)
      .then(() => { this.startConsumerWatchdog(); })
      .finally(() => { this.mediasoupInit = null; });

    return this.mediasoupInit;
  }

  private async ensureMediasoupPeer() {
    await this.mediasoupInit?.catch(() => undefined);
    if (this.mediasoup.isPeerReady(this.user.email) && this.mediasoup.room === this.room) return;
    this.mediasoup.cleanupPeer(this.user.email);
    await this.initMediaSoup();
  }

  initAllSockets() {
    this.user = this.auth.currentUser();
    this.project = this.projectService.currentProjectValue;
    this.room = this.project.database;

    // onConnect() suit le socket courant : après un setUser() l'instance change,
    // un abonnement figé sur ws.socket ne verrait plus jamais de 'connect'.
    this.ws.onConnect().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.user = this.auth.currentUser();
      if (!this.user) return;
      this.project = this.projectService.currentProjectValue;
      this.room = this.project.database;
      this.initMediaSoup();
      this.initSockets();
    });

    if (!this.user) return;
    // // Socket already connected (navigated from another page) — don't wait for 'connect'
    if (this.ws.isConnected()) {
      this.initMediaSoup();
      this.initSockets();
    }
  }

  initSockets() {
    this.discoverRoom();

    this.ws.getConnectedUsers().subscribe((users: SocketUser[]) => {
      this.users = users;
      console.log('----> this.users :>>', this.users);
      this.users.forEach(u => {
        // TODO: use letters avatar
        if (u.avatar?.includes("user_default.png")) u.avatar = "";
      })
    });

    this.streamEventSub?.unsubscribe();
    this.streamEventSub = this.ws.fromStreamEvent().subscribe((event) => {
      const isMySession = this.eventId === event.eventId;

      switch (event.type) {
        case "state": {
          this.joiningRoom = false;
          this.eventStates.set(event.eventId, {
            publisher: event.publisher,
            viewers: event.viewers
          });
          if (!isMySession) break;
          this.publisher = event.publisher;
          this.viewers = event.viewers;
          this.applyBroadcasterState(this.publisher ? null : event.broadcaster);
          this.stateEventId = event.eventId;
          this.prepareTrackerVisit();
          this.attachmentIds = event.attachments || [];
          this.applyRecordingState(event.recording, event.serverNow);
          // this.setEventUsers();
          this.setInvited();

          if (this.publisher?.aspect) this.aspect = this.publisher.aspect;

          if (this.publisher) this.processConsumer(this.publisher);

          this.viewers.forEach((viewer) => {
            this.processConsumer(viewer);
          });
          if (this.attachmentIds.some((id) => !this.fetchedAttachmentIds.has(id))) {
            this.getEventImages(this.attachmentIds);
          }
          let user;
          console.log('----> this.publisher?.connecting :>>', this.publisher?.connecting);
          console.log('----> this.publisher?.streaming :>>', this.publisher?.streaming);
          if (this.isPublisher() && this.publisher?.connecting && this.publisher?.streaming) {
            this.stopLocalPublisher(false);
            this.startMainStream();
            this.publisher.connecting = false;
            user = this.publisher;
          } else if (this.isViewer() && this.getViewer()?.connecting && this.getViewer()?.streaming) {
            this.stopLocalViewer(false);
            this.startViewerStream();
            user = this.getViewer();
            if (user) {
              user.connecting = false;
            }
          } else if (this.isPublisher() && this.publisher?.streaming && !this.stream) {
            this.stopLocalPublisher(false);
            this.startMainStream();
          } else if (this.isViewer() && this.getViewer()?.streaming && !this.viewerStream) {
            this.stopLocalViewer(false);
            this.startViewerStream();
          }
          if (user) this.ws.streamEvent("reconnected", { userId: user?.id });

          if (this.publisher && !this.isPublisher() && this.publisher.streaming && this.publisher.connecting) {
            this.stopLocalPublisher(false);
          }

          this.viewers.forEach((viewer) => {
            if (viewer.connecting && viewer.streaming && !this.isCurrentUser(viewer.email)) {
              this.stopViewer(viewer.email, false);
            }
          });
          this.invited = this.allInvited.filter((u) => !this.isPublisher(u.email) && !this.isViewer(u.email));
          break;
        }
        case "publisher-stopped": {
          if (!isMySession) break;
          if (!this.isCurrentUser(event.email)) this.stopLocalPublisher(false);
          break;
        }
        case "viewer-stopped": {
          if (!isMySession) break;
          if (!this.isCurrentUser(event.email))
            this.stopViewer(event.email, false);
          break;
        }
        case "log": {
          console.log("Log :>>", event.log);
          break;
        }
        case "restart-stream": {
          if (!isMySession) break;
          let streamer = event.streamer;
          if (this.isCurrentUser(streamer.email)) {
            console.warn("Received restart request for my stream");

            let user = this.isPublisher() ? this.publisher : this.getViewer();
            if (!user) return;
            user.camera = !!streamer.camera;
            user.muted = !!streamer.muted;
            if (this.isPublisher()) {
              // this.stopLocalPublisher(false);
              this.startMainStream()
              // setTimeout(() => this.startMainStream(), 300);
            } else if (this.isViewer()) {
              // this.stopLocalViewer(false, streamer.email);
              this.startViewerStream()
              // setTimeout(() => this.startViewerStream(), 300);
            }
          }
          break;
        }
        case "error": {
          // Une erreur serveur sans eventId (handler qui a planté avant de le lire)
          // était silencieusement avalée par le filtre isMySession.
          if (event.eventId && !isMySession) break;
          if (event.error === "invalid_upload" || event.error === "recording_upload_failed") {
            console.error("[recording] the room did not take the upload:", event.error);
            break;
          }
          if (event.error === "not_a_viewer" && this.savedBroadcaster && !this.broadcaster) {
            setTimeout(() => this.restoreSavedBroadcaster(), StreamComponent.BROADCASTER_RESTORE_RETRY_MS);
          }
          const errorKey = `stream.errors.${event.error}`;
          const translated = typeof event.error === "string" ? this.translate.instant(errorKey) : errorKey;
          this.pop.error(translated !== errorKey ? translated : event.error);
          break;
        }
        case "video-upload": {
          this.applyRemoteVideoUpload(event);
          break;
        }
        case "capture-request": {
          if (!isMySession || !this.isPublisher()) break;
          const createdDate = typeof event.createdDate === "string" && !isNaN(Date.parse(event.createdDate))
            ? event.createdDate
            : new Date().toISOString();
          void this.capturePublisherPhoto(createdDate, event.mode === "auto" ? "auto" : "manual");
          break;
        }
        case "event-analyzed": {
          const target = this.events.find(e => e.id === event.eventId);
          if (target) {
            target.message['analyzed'] = event.analyzed;
            this.eventsData.data = [...this.events];
            if (this.event?.id === event.eventId) {
              this.event!.message['analyzed'] = event.analyzed;
            }
          }
          break;
        }
        case "new-room": {
          const ev = event.eventData;
          if (ev?.id && !this.events.find(e => e.id === ev.id)) {
            this.events.unshift(this.handleEvent(ev));
            this.eventsData.data = [...this.events];
          }
          break;
        }
        case "detect": {
          if (!isMySession) break;
          if (!this.isCurrentUser(event.email)) {
            this.detectionsActive = !!event.detect;
          }
          break;
        }
        case "user-added": {
          const target = this.events.find(e => e.id === event.eventId);
          const addedUser = event.addedUser;
          if (target && addedUser?.id) {
            if (event.role === "publisher") {
              target.user = addedUser.id;
              target.host = addedUser;
            } else {
              const users: string[] = target.message["users"] ?? [];
              if (!users.includes(addedUser.id)) users.push(addedUser.id);
              target.message["users"] = users;
              if (!target.invited?.some(u => u.id === addedUser.id)) {
                target.invited = [...(target.invited ?? []), addedUser];
              }
            }
            this.eventsData.data = [...this.events];
            if (this.event?.id === target.id) this.setInvited();
          }
          break;
        }
        case "session-deleted": {
          const deletedId = event.eventId;
          this.events = this.events.filter((e) => e.id !== deletedId);
          this.eventsData.data = [...this.events];
          if (this.event?.id === deletedId) {
            this.event = undefined;
            this.showVisitSidebar = false;
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { event: null },
              queryParamsHandling: 'merge',
            });
          }
          break;
        }
      }
    });

    this.metricSub?.unsubscribe();
    this.metricSub = this.ws.getUpdate("Metric").subscribe((metrics: Risk[]) => {
      if (!metrics?.length) return;
      // Match against all events, not just the currently selected one, so risks
      // appear in the table even after leaving the room.
      const allAttachments = new Set<string>(
        this.events.flatMap(e => e.message?.["attachments"] ?? [])
      );
      const incoming = metrics.filter(
        (m) => m.payload?.["attachment"] && allAttachments.has(m.payload["attachment"] as string)
      );
      if (!incoming.length) return;

      const existingIds = new Set(this.risks.map((r) => r.id));
      const newRisks = incoming.filter((r) => !existingIds.has(r.id));
      if (!newRisks.length) return;

      this.risks = [...this.risks, ...newRisks];
      this.attachRisksToEvents();
      if (this.event) this.captures = [...this.captures];
    });

    this.imageStatusSub?.unsubscribe();
    this.imageStatusSub = this.ws.getUpdateRaw("ImageStatus").subscribe((msg) => {
      if (msg.method !== "UPDATE") return;
      const statuses: ImageStatus[] = msg.data;

      let changed = false;
      statuses.forEach(status => {
        const imageId = typeof status.image === 'string' ? status.image : (status.image as any)?.id;
        let matchingImage: _Image | undefined;
        let matchingEvent: _Event | undefined;
        for (const event of this.events) {
          matchingImage = event.images?.find(img => img.id === imageId);
          if (matchingImage) { matchingEvent = event; break; }
        }
        if (!matchingImage?.attachment) return;

        if (!matchingImage.statuses) matchingImage.statuses = [];
        const statusIdx = matchingImage.statuses.findIndex(s => s.id === status.id);
        if (statusIdx !== -1) {
          matchingImage.statuses[statusIdx] = { ...matchingImage.statuses[statusIdx], ...status };
        } else {
          matchingImage.statuses.push(status);
        }

        // Rebuild captures so Capture.statuses refs stay current.
        if (matchingEvent) {
          matchingEvent.captures = this.buildCaptures(matchingEvent);
        }
        if (matchingEvent?.id === this.event?.id) {
          this.captures = matchingEvent!.captures ?? [];
        }

        const isRiskStatus = !this.labelProject || status.labelProject == this.labelProject.id;
        if (isRiskStatus && !status.labels?.length) {
          this.risks = this.risks.filter(r => r.payload?.['attachment'] !== matchingImage!.attachment);
          changed = true;
        }
      });
      this.eventsData.data = [...this.events];
      if (changed) this.attachRisksToEvents();
    });

    this.imageSyncSub?.unsubscribe();
    this.imageSyncSub = this.ws.getUpdateRaw("Image").subscribe((msg) => {
      const images: _Image[] = msg.data;
      console.log('----> msg.method :>>', msg.method);

      if (msg.method === "DELETE") {
        images.forEach(deleted => {
          const matchingEvent = this.events.find(e => e.id === deleted.event);
          console.log('----> matchingEvent :>>', matchingEvent);
          console.log('----> deleted :>>', deleted);
          if (!matchingEvent) return;
          matchingEvent.images = matchingEvent.images?.filter(img => img.id !== deleted.id) || [];
          matchingEvent.captures = this.buildCaptures(matchingEvent) || [];
          if (deleted.attachment && matchingEvent.message?.["attachments"]) {
            matchingEvent.message["attachments"] = matchingEvent.message["attachments"].filter((a: string) => a !== deleted.attachment);
          }
          if (deleted.attachment) {
            this.risks = this.risks.filter(r => r.payload?.["attachment"] !== deleted.attachment);
            matchingEvent.risks = matchingEvent.risks?.filter(r => r.payload?.["attachment"] !== deleted.attachment)
          }
          if (this.event?.id === matchingEvent.id) {
            this.event.images = [...(matchingEvent.images ?? [])];
            this.captures = matchingEvent.captures;
          }
        });
        this.eventsData.data = [...this.events];
        return;
      }

      if (msg.method === "POST") {
        images.forEach(image => {
          const hseEvent = image.event
            ? this.events.find(e => e.id === image.event)
            : this.events.find(e => e.message?.["attachments"]?.includes(image.attachment));
          if (!hseEvent) return;
          if (hseEvent.images?.find(img => img.id === image.id)) return;
          const isSender = hseEvent.message["attachments"]?.includes(image.attachment);
          this.addAttachmentToEvent(image, hseEvent);
        });
        this.eventsData.data = [...this.events];
        return;
      }

      images.forEach(updated => {
        const matchingEvent = this.events.find(e => e.id === updated.event);
        if (!matchingEvent) return;
        if (matchingEvent.images) {
          const evIdx = matchingEvent.images.findIndex(img => img.id === updated.id);
          if (evIdx !== -1) matchingEvent.images[evIdx] = { ...matchingEvent.images[evIdx], ...updated };
        }
        if (this.event?.id === matchingEvent.id) {
          this.event.images = matchingEvent.images ?? [];
          this.captures = this.buildCaptures(this.event);
        }
      });
      this.eventsData.data = [...this.events];
    });

    this.videoSyncSub?.unsubscribe();
    this.videoSyncSub = this.ws.getUpdateRaw("Video").subscribe((msg) => {
      const videos: _Video[] = msg.data;

      console.log('----> msg :>>', msg);
      if (msg.method === "DELETE") {
        videos.forEach(deleted => {
          const matchingEvent = this.events.find(e => e.id === deleted.event);
          if (!matchingEvent) return;
          if (matchingEvent.videos) {
            matchingEvent.videos = matchingEvent.videos.filter(v => v.id !== deleted.id);
            matchingEvent.captures = this.buildCaptures(matchingEvent);
          }
          if (deleted.attachment && matchingEvent.message?.["attachments"]) {
            matchingEvent.message["attachments"] = matchingEvent.message["attachments"].filter((a: string) => a !== deleted.attachment);
          }
          if (this.event?.id === matchingEvent.id) {
            this.captures = matchingEvent.captures || [];
          }
        });
        this.eventsData.data = [...this.events];
        return;
      }

      if (msg.method === "POST") {
        videos.forEach(video => {
          const hseEvent = this.events.find(e => e.id === video.event);
          if (!hseEvent) return;
          if (!hseEvent.videos) hseEvent.videos = [];
          if (hseEvent.videos.find(v => v.id === video.id)) return;
          hseEvent.videos.push(video);
          const pending = video.uploadId ? this.pendingVideos.get(video.uploadId) : undefined;
          if (pending) {
            pending.placeholder.uploading = false;
            pending.id = video.id;
            pending.attachmentId = video.attachment;
          }
          if (!hseEvent.message["attachments"]) hseEvent.message["attachments"] = [];
          if (video.attachment && !hseEvent.message["attachments"].includes(video.attachment)) {
            hseEvent.message["attachments"].push(video.attachment);
          }
          hseEvent.captures = this.buildCaptures(hseEvent);
          if (this.event?.id === hseEvent.id) {
            this.captures = hseEvent.captures;
          }
        });
        this.eventsData.data = [...this.events];
        return;
      }

      videos.forEach(updated => {
        const matchingEvent = this.events.find(e => e.id === updated.event);
        if (!matchingEvent?.videos) return;
        const idx = matchingEvent.videos.findIndex(v => v.id === updated.id);
        if (idx !== -1) {
          const merged = { ...matchingEvent.videos[idx], ...updated } as _Video;
          if (updated.uploadId && merged.videoSrc) merged.videoSrc = this.withReload(merged.videoSrc);
          matchingEvent.videos[idx] = merged;
        }
        matchingEvent.captures = this.buildCaptures(matchingEvent);
        if (this.event?.id === matchingEvent.id) {
          this.captures = matchingEvent.captures;
        }
      });
      this.eventsData.data = [...this.events];
    });

    this.statusSub?.unsubscribe();
    this.statusSub = this.mediasoup.status.subscribe(async (data: any) => {
      if (!this.hasJoined()) return;

      if (data.type === 'peer-disconnected') {
        console.warn('[stream] peer-disconnected for', data.peerId, '- waiting for protoo to reconnect');
        this.scheduleDisconnectRecovery(data.peerId);
        return;
      }

      // Emitted before the rebuilt device joins, so this must stay synchronous:
      // the server sends its newConsumer requests as soon as the join lands, and
      // isStreaming() would still report the frozen track from the dead transport
      // and skip them.
      if (data.type === 'peer-rejoining') {
        console.warn('[stream] peer-rejoining for', data.peerId, '- dropping stale remote video');
        this.cancelDisconnectRecovery(data.peerId);
        this.clearRemoteVideoSrcObjects();
        return;
      }

      if (data.type === 'peer-rejoined') {
        console.warn('[stream] peer-rejoined for', data.peerId);
        // Not data.producing: every connectStream() call from here passes
        // isProducer false and adds the producer afterwards via initiateProducer(),
        // so that flag never reflects whether we are actually streaming.
        // restartLocalProducer() reads the role's own state and no-ops otherwise.
        await this.restartLocalProducer();
        return;
      }

      if (data.type === 'peer-closed') {
        console.warn('[stream] peer-closed received for', data.peerId, '| hasJoined:', this.hasJoined(), '| visibility:', document.visibilityState);
        // If the tab is hidden the browser will close any new WS connection immediately too.
        // Skip — onVisibilityChange will reconnect once the tab is visible again.
        if (document.visibilityState === 'hidden') return;
        await this.reconnectAsConsumer(data.peerId);
        return;
      }

      if (this.activeBroadcaster && data.broadcasterId === this.activeBroadcaster) {
        if (data.type === 'producer-ended' && this.isRecorder()) this.closeSegment();
        if (data.type === 'producer-ended') {
          this.trackerStreamLive = false;
          if (this.trackerVideo) this.trackerVideo.nativeElement.srcObject = null;
          this.waitForTrackerStream();
        } else if (data.producerId) {
          this.attachTrackerStream();
          if (this.recording && this.isRecorder() && !this.isRecording) setTimeout(() => this.resumeRecording(), 1000);
        }
        return;
      }

      if (data.type === 'producer-ended') {
        console.warn('[stream] producer-ended for', data.broadcasterId);
        const video = this.getVideo(data.broadcasterId);
        if (video) video.srcObject = null;
        return;
      }

      // Our own receive/send transport is being rebuilt: show a loader over the remote
      // videos and freeze the aspect ratio until it recovers, instead of a blank square.
      if (data.type === 'reconnecting') {
        if (data.peerId === this.user.email) {
          if (this.reconnectingPeers.size === 0) this.aspectSnapshot = this.aspect;
          this.reconnectingPeers.add(data.peerId);
          this.markRemotesConnecting(true);
        }
        return;
      }
      if (data.type === 'reconnected' || data.type === 'reconnect-failed') {
        this.reconnectingPeers.delete(data.peerId);
        if (this.reconnectingPeers.size === 0) {
          this.markRemotesConnecting(false);
          this.aspectSnapshot = undefined;
        }
        return;
      }

      let email = data.broadcasterId;
      let user = this.isPublisher(email)
        ? this.publisher
        : this.getViewer(email);
      if (user && !this.isCurrentUser(user.email)) {
        if (this.isPublisher(email)) {
          this.processConsumer(user);
        } else {
          setTimeout(() => this.consumeStream(email), 100);
        }
      }
      if (data.type == "producer-ready" && this.isCurrentUser(email))
        this.producerReady = true;
    });
  }

  async initWebrtc(peerId: string, isProducer: boolean = false) {
    if (!this.isConnected) {
      let role = this.isPublisher() ? "publisher" : "viewer";
      await this.mediasoup.connectStream(peerId, this.room, true, isProducer);
      // if (this.isConnected && isProducer)
      await this.mediasoup.initiateProducer(peerId);
    }
    this.isConnected = true;
  }

  async initGPS() {
    if (!this.isPublisher()) return;
    this.gpsTracker = await this.resolveUserTracker(this.user.email);
    await this.gps.initCompass();
    this.gps.startWatching();
    this.gpsDestroy$ = new Subject<void>();
    this.gps.position$
      .pipe(takeUntil(this.gpsDestroy$))
      .subscribe(({ coords, orientation }) => {
        if (!coords || !this.gpsTracker || !this.isStreaming()) return;
        this.addTrack(coords, orientation);
      });
  }

  //------------------- HostListener events ---------------------------------------
  @HostListener("window:aspect-change", ["$event"]) onWindowAspectChange(
    event: CustomEvent,
  ) {
    let { isLandscape, aspect } = event.detail;
    this.isLandscape = isLandscape;
    if (!this.isPublisher()) return;
    let maxWidth = this.maxWidth;
    let maxHeight = this.maxHeight;
    if (maxWidth < 768) this.isMobile = true;
    if (this.isLandscape) {
      this.aspect = "16:9";
      this.maxWidth = Math.max(maxWidth, maxHeight);
      this.maxHeight = Math.min(maxWidth, maxHeight);
    } else {
      // Portrait mode is retired — holding the phone upright renders square, not 9:16.
      this.aspect = "1:1";
      this.isLandscape = false;
      this.maxWidth = Math.min(maxWidth, maxHeight);
      this.maxHeight = Math.max(maxWidth, maxHeight);
    }

    this.onAspectChange({ value: this.aspect } as MatButtonToggleChange);
  }

  @HostListener("document:visibilitychange")
  async onVisibilityChange() {
    const state = document.visibilityState;
    console.warn('[visibility] changed to', state, '| hasJoined:', this.hasJoined(), '| isPeerReady:', this.mediasoup.isPeerReady(this.user.email));
    if (state !== "visible") return;
    if (!this.hasJoined()) {
      console.warn('[visibility] not joined, skipping reconnect');
      return;
    }
    // If the pipeline survived the background period, keep streaming — don't restart.
    // A connection that actually dropped will surface as a peer-disconnected/peer-closed
    // event (now that we're visible again) and reconnect through that path.
    if (this.isStreamHealthy()) {
      console.warn('[visibility] stream still healthy, skipping restart');
      return;
    }
    await this.restartStreams();

  }

  /** True when the current peer transport is connected and the active role's tracks are live. */
  private isStreamHealthy(): boolean {
    if (!this.mediasoup.isPeerReady(this.user.email)) return false;
    if (this.isPublisher() && this.publisher?.streaming) {
      return this.stream?.getTracks().some(t => t.readyState === 'live') ?? false;
    }
    if (this.isViewer() && this.getViewer()?.streaming) {
      return this.viewerStream?.getTracks().some(t => t.readyState === 'live') ?? false;
    }
    // Not actively streaming a local track (e.g. pure observer) — nothing to restart.
    return true;
  }

  async restartStreams() {
    // Always attempt consumer reconnect on visibility restore — the connection
    // may have dropped while the browser was hidden, and isPeerReady is unreliable
    // at this point because the WebSocket close event may not have fired yet.
    await this.reconnectAsConsumer(this.user.email);
    await this.restartLocalProducer();
  }

  /** Re-establish the producer transport if we were streaming. A consumer
   * reconnect only rebuilds the consumer side, so the producer must be
   * re-initialized explicitly. If the local stream is still alive we reuse it;
   * otherwise we restart from scratch (getUserMedia). */
  private async restartLocalProducer() {
    if (this.isPublisher() && this.publisher?.streaming) {
      const publisherTracksLive = this.stream?.getTracks().some(t => t.readyState === 'live') ?? false;
      if (!this.stream || !publisherTracksLive) {
        console.warn('[visibility] restarting publisher stream (tracks ended or no stream)');
        this.stopLocalPublisher(false);
        await this.startMainStream();
      } else {
        console.warn('[visibility] re-establishing producer transport');
        await this.mediasoup.initiateProducer(this.user.email);
        await this.produceStream(this.user.email, this.stream);
      }
    } else if (this.isViewer() && this.getViewer()?.streaming) {
      const viewerTracksLive = this.viewerStream?.getTracks().some(t => t.readyState === 'live') ?? false;
      if (!this.viewerStream || !viewerTracksLive) {
        console.warn('[visibility] restarting viewer stream (tracks ended or no stream)');
        this.stopLocalViewer(false);
        await this.startViewerStream();
      } else {
        console.warn('[visibility] re-establishing viewer producer transport');
        await this.mediasoup.initiateProducer(this.user.email);
        await this.produceStream(this.user.email, this.viewerStream);
      }
    }

  }

  //------------------- API/Fetching ----------------------------------------------
  resolveUserTracker(email: string): Promise<Tracker> {
    const cached = this.userTrackers.get(email);
    if (cached) return cached;
    const pending = this.fetchOrCreateTracker(email)
      .then(async (tracker) => {
        this.userUnits.set(email, await this.ensureUserUnit(email, tracker));
        return tracker;
      })
      .catch((err) => {
        this.userTrackers.delete(email);
        const errMessage = toError(err);
        this.pop.error(errMessage);
        throw errMessage;
      });
    this.userTrackers.set(email, pending);
    return pending;
  }

  private async linkTrackerToEvent(tracker: { id: string; name: string }, eventId?: string) {
    if (!eventId) return;
    const targetEvent = this.events.find(e => e.id === eventId) ?? (eventId === this.event?.id ? this.event : undefined);
    if (!targetEvent) return;
    const isPublisher = tracker.name === targetEvent.host?.email
      || (eventId === this.event?.id && tracker.name === (this.publisher?.email ?? this.trackerBroadcaster));
    const unit = this.userUnits.get(tracker.name) ?? await this.findUnit(tracker.name).catch(() => undefined);

    const idOf = (ref?: string | { id: string }) => typeof ref === 'string' ? ref : ref?.id;
    const includes = (refs: (string | { id: string })[] | undefined, id: string) => (refs ?? []).some(ref => idOf(ref) === id);

    const addToSet: Record<string, string> = {};
    const set: Record<string, string> = {};
    if (!includes(targetEvent.trackers, tracker.id)) addToSet['trackers'] = tracker.id;
    if (unit && !includes(targetEvent.units, unit.id)) addToSet['units'] = unit.id;
    if (isPublisher && idOf(targetEvent.tracker) !== tracker.id) set['tracker'] = tracker.id;
    if (isPublisher && unit && idOf(targetEvent.unit) !== unit.id) set['unit'] = unit.id;
    if (!Object.keys(addToSet).length && !Object.keys(set).length) return;

    if (addToSet['trackers']) targetEvent.trackers = [...(targetEvent.trackers ?? []), tracker.id] as string[];
    if (addToSet['units']) targetEvent.units = [...(targetEvent.units ?? []), unit!.id] as string[];
    if (set['tracker']) targetEvent.tracker = tracker.id;
    if (set['unit']) targetEvent.unit = unit!.id;

    const update: any = { id: eventId };
    if (Object.keys(addToSet).length) update.$addToSet = addToSet;
    if (Object.keys(set).length) update.$set = set;
    this.eventService.updateEvent(update).pipe(first()).subscribe({ error: () => {} });
  }

  private buildFileName(trackerId: string, createdDate: string, extension: string): string {
    return `${this.project?.database}_hse-${trackerId.slice(-8)}_${format(createdDate, "yyyyMMdd-HHmmss")}.${extension}`;
  }

  private async findTracker(email: string): Promise<Tracker | undefined> {
    const trackers: Tracker[] = await firstValueFrom(this.trackerService.getTrackersLight({ name: email }));
    return trackers.find((t) => t.name === email);
  }

  private async fetchOrCreateTracker(email: string): Promise<Tracker> {
    const existing = await this.findTracker(email);
    if (existing) return existing;
    try {
      return await firstValueFrom(this.trackerService.addTracker({ name: email, project: this.project.id }));
    } catch (err) {
      const created = await this.findTracker(email);
      if (created) return created;
      throw err;
    }
  }

  private async findUnit(email: string): Promise<Unit | undefined> {
    const units = await firstValueFrom(this.unitService.findUnits({ name: email }));
    return units.find((u) => u.name === email);
  }

  private async ensureUserUnit(email: string, tracker: Tracker): Promise<Unit> {
    let unit = await this.findUnit(email);
    if (!unit) {
      try {
        return await firstValueFrom(this.unitService.addUnit({ name: email, trackers: [tracker.id], projects: [this.project.id], type: 'camera' }));
      } catch (err) {
        unit = await this.findUnit(email);
        if (!unit) throw err;
      }
    }
    const trackerIds = (unit.trackers ?? []).map((t) => typeof t === 'string' ? t : t.id);
    if (!trackerIds.includes(tracker.id)) {
      await firstValueFrom(this.unitService.updateUnit({ id: unit.id, trackers: [...trackerIds, tracker.id] }));
    }
    return unit;
  }

  getStreamEvents(ids?: any) {
    this.loadingEvents = true;
    let query: any = { type: "HSE", populated: true }
    if (ids?.length) query.ids = ids as any[];
    this.eventService.getEvents(query).subscribe({
      next: async (events: _Event[]) => {
        events = events.map(event => {
          event = this.handleEvent(event);
          this.loadingReport[event.id] = false;
          return event;
        });
        this.events = events;
        if (this.event) {
          const newEventRef = this.events.find(e => e.id === this.event!.id);
          if (newEventRef) {
            // API may not populate images; preserve what we accumulated in memory
            if (this.event.images?.length && !newEventRef.images?.length) {
              newEventRef.images = this.event.images;
              newEventRef.videos = this.event.videos;
              newEventRef.captures = this.buildCaptures(newEventRef);
            }
            this.event = newEventRef;
            this.captures = this.buildCaptures(this.event);
          }
          // else: event not in new list (project changed) — keep event/captures as-is until cleared by effect
        } else {
          this.event = this.events.find(event => event.id == this.eventId);
          this.captures = this.event ? this.buildCaptures(this.event) : [];
        }
        this.eventsData.data = events;
        this.setInvited();
        this.prepareTrackerVisit();
        if (events.length) await this.getRisks();
        this.loadingEvents = false;
      },
      error: (err) => {
        this.pop.error(toError(err));
        this.loadingEvents = false;
      },
    });
  }

  addTrack(coords: GeolocationCoordinates, orientation: number) {
    let createdDate = new Date().toISOString();
    let [x, y] = getXYFromLatLonAlt2(coords.latitude, coords.longitude);
    let track: Track = {
      id: "",
      lat: coords.latitude || 0,
      lon: coords.longitude || 0,
      z: coords.altitude || 0,
      x: x || 0,
      y: y || 0,
      tracker: this.gpsTracker!.id,
      trackerName: this.gpsTracker!.name,
      createdDate,
      message: {
        orientation,
        user: this.publisher?.email,
      },
    };
    this.trackService
      .addTrack(track)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {});
  }

  async getRisks(events?: _Event[], doFetch: boolean = false) {
    events = events ?? this.events;
    return new Promise((resolve) => {
      if (!events?.length) return resolve([]);
      // if (!events.some(event => event.images?.length)) return resolve([]);
      if (this.risks.length && !doFetch) {
        this.attachRisksToEvents();
        return resolve(this.risks);
      }
      this.risks = [];

      const query: any = {
        name: "Risque",
        // attachments: events.flatMap(event => [...event.message["attachments"]]),
        limit: 10000,
      };

      this.metricService
        .getMetrics(query)
        .pipe(takeUntil(this.destroy$))
        .subscribe((risks) => {
          this.risks = risks;
          this.attachRisksToEvents();
          resolve(this.events);
        });
    });
  }

  getEventImages(attachments?: string[]) {
    attachments = attachments ?? this.event?.message["attachments"];
    if (!this.event || !attachments?.length) {
      this.captures = [];
      this.fetchedAttachmentIds.clear();
      return;
    }
    const requested = attachments;
    this.imageService
      .getImages({
        attachments,
        labels: true,
      })
      .subscribe((images) => {
        // The bulk route omits ids that no longer resolve, so track what was
        // asked for: a deleted id would otherwise refetch the gallery for ever.
        requested.forEach((id) => this.fetchedAttachmentIds.add(id));
        const known = new Set(this.captures.map((c) => c.attachment).filter(Boolean));
        const fresh = images.filter((img) => !known.has(img.attachment));
        this.captures = this.captures.concat(fresh.map(img => this.mapImageToCapture(img)));
        images.forEach(image => this.addAttachmentToEvent(image));
      });
  }

  private async postAttachment(
    base64: string,
    type: "photo" | "video",
    camera: CameraType = "carried",
    targetEvent?: _Event,
    filenameDate?: string,
    showSuccessPopup = false,
    showPlaceholder = true,
    source?: { id: string; name: string },
  ) {
    const activeEvent = targetEvent ?? this.event;
    const isCurrentEvent = !targetEvent || targetEvent.id === this.event?.id;
    const matchingEvent = this.events.find(e => e.id === activeEvent?.id);
    const sourceEmail = source?.name ?? this.publisher?.email ?? this.user.email;

    const createdDate = filenameDate ?? new Date().toISOString();
    const placeholder = this.getPlaceholder({ type, createdDate, trackerName: sourceEmail });
    if (showPlaceholder && matchingEvent) {
      matchingEvent.captures = (matchingEvent.captures ?? []).concat(placeholder);
    }

    if (this.isAutoCapturing) this.autoCaptureCreatedDates.add(createdDate);
    if (showPlaceholder && isCurrentEvent) this.captures.push(placeholder);

    let tracker: { id: string; name: string };
    try {
      tracker = source ?? await this.resolveUserTracker(sourceEmail);
    } catch {
      if (isCurrentEvent) this.captures = this.captures.filter((c) => c.id !== placeholder.id);
      return;
    }
    const name = this.buildFileName(tracker.id, createdDate, type === "photo" ? "jpg" : "mp4");

    this.attachmentService
      .postWithPredict({
        name,
        createdDate,
        path: base64,
        tracker: tracker.id,
        camera,
        event: activeEvent?.id,
        fromHse: true,
        cameraView: this.cameraViewFor(activeEvent, tracker.id),
        source: 'capture',
      })
      .subscribe({
        next: async (attachment) => {
          if (isCurrentEvent) {
            await this.updateStreamEvent(attachment);
          } else if (activeEvent) {
            activeEvent.message["attachments"] = [
              ...(activeEvent.message["attachments"] ?? []),
              attachment.id,
            ];
            this.eventService.updateEvent({
              id: activeEvent.id,
              "message.attachments": activeEvent.message["attachments"],
            } as any).pipe(first()).subscribe();
          }
          this.linkTrackerToEvent(tracker, isCurrentEvent ? this.event?.id : activeEvent?.id);
          if (showSuccessPopup) this.pop.success(this.translate.instant(`stream.upload.success`), "success", "upload");
        },
        error: (err) => {
          if (isCurrentEvent) this.captures = this.captures.filter((c) => c.id !== placeholder.id);
          this.pop.error(toError(err));
        },
      });
  }

  private batchCaptures<T extends { path?: string }>(payloads: T[]): T[][] {
    const batches: T[][] = [];
    let current: T[] = [];
    let bytes = 0;
    for (const payload of payloads) {
      const size = payload.path?.length ?? 0;
      if (current.length && (current.length >= StreamComponent.CAPTURE_BATCH_SIZE || bytes + size > StreamComponent.CAPTURE_BATCH_BYTES)) {
        batches.push(current);
        current = [];
        bytes = 0;
      }
      current.push(payload);
      bytes += size;
    }
    if (current.length) batches.push(current);
    return batches;
  }

  private async postManyCaptures(
    frames: { base64: string; createdDate: string }[],
    videoId: string,
    videoAttachmentId: string,
    targetEvent?: _Event,
    isAuto?: boolean,
    source?: { id: string; name: string },
  ): Promise<void> {
    if (!frames.length) return;
    const activeEvent = targetEvent ?? this.event;
    const isCurrentEvent = !targetEvent || targetEvent.id === this.event?.id;
    const trackerName = source?.name ?? this.user.email;

    const placeholders = frames.map(f => this.getPlaceholder({ type: 'photo', createdDate: f.createdDate, trackerName }));
    if (activeEvent) {
      if (!activeEvent.captures) activeEvent.captures = [];
      activeEvent.captures.push(...placeholders);
    }
    if (isCurrentEvent) this.captures = activeEvent ? [...activeEvent.captures!] : [...this.captures, ...placeholders];
    this.eventsData.data = [...this.events];

    let tracker: { id: string; name: string };
    try {
      tracker = source ?? await this.resolveUserTracker(this.user.email);
    } catch {
      const ids = new Set(placeholders.map(p => p.id));
      if (activeEvent?.captures) activeEvent.captures = activeEvent.captures.filter(c => !ids.has(c.id));
      if (isCurrentEvent) this.captures = (activeEvent?.captures) ?? this.captures.filter(c => !ids.has(c.id));
      else this.eventsData.data = [...this.events];
      return;
    }

    const payloads: PostAttachment[] = frames.map(f => ({
      name: this.buildFileName(tracker.id, f.createdDate, 'jpg'),
      path: f.base64,
      createdDate: f.createdDate,
      tracker: tracker.id,
      camera: 'carried' as CameraType,
      event: activeEvent?.id,
      video: videoId,
      parent: videoAttachmentId,
      fromHse: true,
      cameraView: this.cameraViewFor(activeEvent, tracker.id),
      source: 'capture',
    }));

    const batches = this.batchCaptures(payloads);
    let sent = 0;
    for (const batch of batches) {
      try {
        const result = await firstValueFrom(this.attachmentService.postManyAttachments(batch));
        sent += batch.length;
        if (!activeEvent || !result.created.length) continue;
        if (!activeEvent.message['attachments']) activeEvent.message['attachments'] = [];
        activeEvent.message['attachments'] = [
          ...activeEvent.message['attachments'],
          ...result.created.map(a => a.id),
        ];
        this.eventService.updateEvent({
          id: activeEvent.id,
          'message.attachments': activeEvent.message['attachments'],
        } as any).pipe(first()).subscribe();
        this.linkTrackerToEvent(tracker, activeEvent.id);
      } catch (err) {
        const ids = new Set(placeholders.slice(sent).map(p => p.id));
        if (activeEvent?.captures) activeEvent.captures = activeEvent.captures.filter(c => !ids.has(c.id));
        if (isCurrentEvent) this.captures = (activeEvent?.captures) ?? this.captures.filter(c => !ids.has(c.id));
        else this.eventsData.data = [...this.events];
        this.pop.error(toError(err));
        return;
      }
    }

    if (isAuto) this.pop.success(this.translate.instant(`stream.upload.capture${payloads.length > 1 ? 's' : ''}`), "success", "upload");
  }

  /**
   * Upload several user-selected images in a single request (via
   * `postManyAttachments`) instead of one request per image, so the backend
   * can process the whole batch together. Mirrors postManyCaptures but for
   * standalone uploads (no parent video).
   */
  private async postManyAttachments(
    items: { base64: string; createdDate: string }[],
    camera: CameraType = "carried",
    showSuccessPopup = false,
    batchId?: string,
  ): Promise<void> {
    if (!items.length) return;

    if (!this.event) await this.updateStreamEvent();
    this.claimUploaderRole();
    const activeEvent = this.event;

    const placeholders = items.map(it => this.getPlaceholder({ type: 'photo', createdDate: it.createdDate }));
    if (activeEvent) {
      if (!activeEvent.captures) activeEvent.captures = [];
      activeEvent.captures.push(...placeholders);
      this.captures = [...activeEvent.captures];
    } else {
      this.captures = [...this.captures, ...placeholders];
    }

    let tracker: Tracker;
    try {
      tracker = await this.resolveUserTracker(this.user.email);
    } catch {
      const ids = new Set(placeholders.map(p => p.id));
      if (activeEvent?.captures) activeEvent.captures = activeEvent.captures.filter(c => !ids.has(c.id));
      this.captures = (activeEvent?.captures) ?? this.captures.filter(c => !ids.has(c.id));
      return;
    }

    const payloads: PostAttachment[] = items.map(it => ({
      name: this.buildFileName(tracker.id, it.createdDate, 'jpg'),
      path: it.base64,
      createdDate: it.createdDate,
      tracker: tracker.id,
      camera,
      event: activeEvent?.id,
      fromHse: true,
      source: 'upload',
      batchId,
    }));

    try {
      const result = await firstValueFrom(this.attachmentService.postManyAttachments(payloads));
      if (activeEvent && result.created.length) {
        if (!activeEvent.message['attachments']) activeEvent.message['attachments'] = [];
        activeEvent.message['attachments'] = [
          ...activeEvent.message['attachments'],
          ...result.created.map(a => a.id),
        ];
        await firstValueFrom(this.eventService.updateEvent({
          id: activeEvent.id,
          'message.attachments': activeEvent.message['attachments'],
        } as any));
        this.linkTrackerToEvent(tracker, activeEvent.id);
      }
      if (showSuccessPopup) this.pop.success(this.translate.instant(`stream.upload.success`), "success", "upload");
    } catch (err) {
      const ids = new Set(placeholders.map(p => p.id));
      if (activeEvent?.captures) activeEvent.captures = activeEvent.captures.filter(c => !ids.has(c.id));
      this.captures = (activeEvent?.captures) ?? this.captures.filter(c => !ids.has(c.id));
      this.pop.error(toError(err));
    }
  }

  /** Stop every local capture track so the camera/mic is released before (re)acquiring it.
   * Prevents "camera already in use" when switching role/session (e.g. viewer → operator). */
  private releaseCameraTracks() {
    for (const s of [this.stream, this.viewerStream]) {
      try { s?.getTracks().forEach((t: MediaStreamTrack) => t.stop()); } catch {}
    }
    this.stream = null;
    this.viewerStream = null;
  }

  /** getUserMedia with a single retry: the camera can still be releasing from a previous
   * stream (common on mobile), which surfaces as NotReadableError/AbortError. */
  private async acquireCamera(constraints: MediaStreamConstraints): Promise<MediaStream> {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err: any) {
      if (err?.name === 'NotReadableError' || err?.name === 'AbortError') {
        await new Promise(r => setTimeout(r, 400));
        return await navigator.mediaDevices.getUserMedia(constraints);
      }
      throw err;
    }
  }

  // ---------- Stream event startup ---------------------------------------------
  async startMainStream() {
    let publisherVideoEl = this.publisherVideo?.nativeElement;
    if (!publisherVideoEl) {
      await new Promise(res => setTimeout(res, 0));
      publisherVideoEl = this.publisherVideo?.nativeElement;
    }
    if (!publisherVideoEl) {
      console.warn('startMainStream: publisherVideo not available, skipping');
      return;
    }
    try {
      this.updateParams();
      let camEnabled = this.publisherParams.includes("camera");
      let micEnabled = this.publisherParams.includes("mic");
      const initConstraints = this.getVideoConstraints(this.aspect || this.getOrientation());
      // Free any camera/mic still held by a previous (e.g. viewer) stream first.
      this.releaseCameraTracks();
      this.stream = await this.acquireCamera({
        video: {
          facingMode: this.currentFacingMode,
          ...initConstraints,
        },
        audio: true,
      });
      this.stream
        .getVideoTracks()
        .forEach((track) => (track.enabled = camEnabled));
      this.stream
        .getAudioTracks()
        .forEach((track) => (track.enabled = micEnabled));
      this.isLandscape = this.isMobile && screen.orientation.type.startsWith('landscape');

      this.updateAspectRatio();

      this.detectCameras();

      await this.ensureMediasoupPeer();
      await this.mediasoup.initiateProducer(this.user.email);

      // Attendre que le transport soit connecté
      const waitForConnection = new Promise<void>((resolve) => {
        const subscription = this.mediasoup.status.subscribe((data: any) => {
          if (
            data.type === "producer-ready" &&
            data.producerId === this.user.email!
          ) {
            subscription.unsubscribe();
            resolve();
          }
        });
      });

      await this.playVideo(
        publisherVideoEl,
        this.stream!,
        this.user.email,
      );

      await this.produceStream(this.user.email!, this.stream!);

      await waitForConnection;
      let track = this.stream.getVideoTracks()[0];
      let caps = track?.getSettings();

      this.publisher!.streaming = true;
      this.ws.streamEvent("publisher-start");
      this.startWatchdog();
    } catch (err) {
      console.error('[startMainStream] error:', err);
      if (this.stream) {
        // Camera acquired but mediasoup failed — stop and retry
        this.stopLocalPublisher(false);
        await new Promise(res => setTimeout(res, 2000));
        if (this.hasJoined()) await this.startMainStream();
      } else {
        this.pop.error(this.getMediaErrorMessage(err));
      }
    }
  }

  /** Traduit les erreurs getUserMedia en message clair et actionnable. */
  private getMediaErrorMessage(err: any): string {
    switch (err?.name) {
      case "NotAllowedError":
      case "SecurityError":
        return "Accès à la caméra/micro refusé. Autorisez la caméra dans les paramètres du navigateur (icône cadenas/caméra dans la barre d'adresse), puis réessayez.";
      case "NotFoundError":
      case "OverconstrainedError":
        return "Aucune caméra ou micro détecté sur cet appareil.";
      case "NotReadableError":
        return "La caméra est déjà utilisée par une autre application. Fermez-la puis réessayez.";
      default:
        return "Erreur lors de l'accès à la caméra";
    }
  }

  async startViewerStream() {
    try {
      // Free any camera/mic still held by a previous (e.g. publisher) stream first.
      this.releaseCameraTracks();
      this.viewerStream = await this.acquireCamera({
        video: {
          facingMode: this.viewerFacingMode,
          width: 240,
          height: 240,
        },
        audio: true,
      });

      this.updateParams();
      this.viewerStream
        .getVideoTracks()
        .forEach(
          (track) => (track.enabled = this.viewerParams.includes("camera")),
        );
      this.viewerStream
        .getAudioTracks()
        .forEach(
          (track) => (track.enabled = this.viewerParams.includes("mic")),
        );

      await this.ensureMediasoupPeer();
      await this.mediasoup.initiateProducer(this.user.email);

      // Attendre que le transport soit connecté
      const waitForConnection = new Promise<void>((resolve) => {
        const subscription = this.mediasoup.status.subscribe(
          async (data: any) => {
            if (
              data.type === "producer-ready" &&
              this.isViewer(data.producerId)
            ) {
              subscription.unsubscribe();
              resolve();
            }
          },
        );
      });

      let viewerIndex = this.viewers.findIndex(
        (v) => v.email == this.user.email,
      );
      if (viewerIndex == -1) return;
      let viewer = this.viewers[viewerIndex];
      let viewerVideo = this.viewerVideos.toArray()[viewerIndex];
      if (!viewerVideo || !viewer) return;

      await this.playVideo(
        viewerVideo.nativeElement,
        this.viewerStream!,
        this.user.email,
      );

      await this.produceStream(this.user.email, this.viewerStream);

      await waitForConnection;

      viewer.streaming = true;
      this.ws.streamEvent("viewer-start");
      this.startWatchdog();
    } catch (err) {
      console.error("Error accessing viewer camera:", err);
      this.pop.error(this.getMediaErrorMessage(err));
    }
  }

  async consumeStream(producerId: string) {
    let remoteVideo = this.getVideo(producerId);
    const stream = this.mediasoup.getConsumerStream(producerId);
    console.log('[consumeStream]', producerId, '| hasVideo:', !!remoteVideo, '| hasStream:', !!stream);
    if (!remoteVideo) {
      console.warn('[consumeStream] no video element found for', producerId);
      return;
    }

    if (!stream) {
      console.warn('[consumeStream] no stream in mediasoup map for', producerId);
      return;
    }
    // if (this.isCurrentUser(producerId)) {
    //   stream.getAudioTracks().forEach(track => track.enabled = false);
    //   console.log('disabled :>> ', stream);
    // } else {
    //   console.log("NOT CURRENT USER");
    // }

    await this.playVideo(remoteVideo, stream, producerId);
  }

  produceStream(peerId: string, stream: MediaStream) {
    stream.getTracks().forEach(async (track) => {
      await this.mediasoup.produceTrack(peerId, track);
    });
  }

  async detectCameras() {
    if (!navigator.mediaDevices?.enumerateDevices) {
      this.hasMultipleCameras = false;
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((d) => d.kind === "videoinput");

    this.hasMultipleCameras = videoInputs.length > 1; // || isDevMode();
    this.videoDevices = videoInputs;
  }

  // --------------- Stream management functions -----------------------------------
  private startWatchdog() {
    this.stopWatchdog();
    this.watchdogLastTime = -1;
    this.watchdogInterval = setInterval(() => {
      const videoEl = this.getVideo(this.user.email);
      const stream = this.stream ?? this.viewerStream;
      if (!videoEl || !stream || this.isSwitchingCamera) return;
      const now = videoEl.currentTime;
      if (this.watchdogLastTime >= 0 && now <= this.watchdogLastTime) {
        console.warn('[watchdog] Local video frozen, recovering...');
        videoEl.srcObject = null;
        videoEl.srcObject = stream;
        videoEl.play().catch(() => {});
      }
      this.watchdogLastTime = now;
    }, 2000);
  }

  private stopWatchdog() {
    clearInterval(this.watchdogInterval);
    this.watchdogInterval = null;
  }

  stopLocalPublisher(emit: boolean = true) {
    if (!this.hasJoined()) return;
    this.stopWatchdog();
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      if (emit) this.ws.streamEvent("publisher-stop");
      this.mediasoup.stopProducing(this.user.email);
    }
    if (this.publisherVideo) this.publisherVideo.nativeElement.srcObject = null;

    // The camera track is gone, so the segment in flight is over.
    this.closeSegment();
    if (this.isPublisher()) this.gps.destroy();
  }

  stopStream() {
    if (this.isPublisher() && this.publisher?.streaming) {
      this.stopLocalPublisher();
    } else if (this.isViewer() && this.getViewer()?.streaming)
      this.stopLocalViewer();
  }

  stopRemoteStream(producerId: string) {
    this.mediasoup.stopProducing(producerId);
  }

  stopViewer(peerId: string, emitEvent: boolean = true) {
    if (!this.isCurrentUser(peerId)) {
      let video = this.getVideo(peerId);
      if (video) video.srcObject = null;
    } else this.stopLocalViewer(emitEvent);
  }

  stopLocalViewer(emit: boolean = true, email?: string) {
    this.stopWatchdog();
    if (this.viewerStream) {
      this.viewerStream.getTracks().forEach((track) => track.stop());
      this.viewerStream = null;
      if (emit) this.ws.streamEvent("viewer-stop", { room: this.room });
    }
    let viewerVideo = this.getVideo(email ?? this.user.email);
    if (viewerVideo) viewerVideo.srcObject = null;
  }

  stopViewerStream() {
    if (this.isViewer()) {
      this.stopLocalViewer();
    }
  }

  askForRestartStream(email: string) {
    if (!email || this.isCurrentUser(email)) return;

    console.warn("Requesting stream restart for:", email);
    let user = this.isPublisher(email) ? this.publisher : this.getViewer(email);
    this.ws.streamEvent("restart-stream", { userId: user?.id });
  }

  private clearRemoteVideoSrcObjects() {
    const remoteUsers: SocketUser[] = [
      ...(this.publisher && !this.isCurrentUser(this.publisher.email) ? [this.publisher] : []),
      ...this.viewers.filter(v => !this.isCurrentUser(v.email)),
    ];
    for (const user of remoteUsers) {
      const video = this.getVideo(user.email);
      if (video) {
        console.warn('[reconnect] Clearing srcObject for', user.email);
        video.srcObject = null;
      }
    }
    if (this.trackerVideo) this.trackerVideo.nativeElement.srcObject = null;
  }

  /** protoo emits 'disconnected' then reconnects the WebSocket by itself (10 retries,
   * 1s to 8s backoff) and re-joins on success. Rebuilding the peer right away raced
   * that retry and left two peers for the same email. Wait it out, and only rebuild
   * if the socket is still down. */
  /** Last-resort net under every other recovery path: watch the inbound RTP
   * counters of the streams we consume and rebuild when they stop advancing.
   * Deliberately subordinate -- it stands down whenever another recovery is
   * already running, because two of them racing is what caused the rebuild
   * loops this whole series of fixes was about. */
  private startConsumerWatchdog() {
    if (this.consumerWatchdogInterval) return;
    this.consumerStallCounts.clear();
    this.consumerLastProgress.clear();
    this.consumerRecoveries = 0;
    console.warn('[consumer-watchdog] armed | every', StreamComponent.CONSUMER_CHECK_MS, 'ms');
    this.consumerWatchdogInterval = setInterval(() => {
      void this.checkConsumedStreams();
    }, StreamComponent.CONSUMER_CHECK_MS);
  }

  private stopConsumerWatchdog() {
    if (this.consumerWatchdogInterval) clearInterval(this.consumerWatchdogInterval);
    this.consumerWatchdogInterval = null;
    this.consumerStallCounts.clear();
    this.consumerLastProgress.clear();
  }

  /** True while some other mechanism owns the recovery, or while a stall would
   * be meaningless (hidden tab, peer already down, nothing joined). */
  private consumerWatchdogShouldStandDown(): boolean {
    return !this.hasJoined()
      || document.visibilityState !== 'visible'
      || this.isReconnecting
      || this.disconnectRecoveryTimers.size > 0
      || !this.mediasoup.isPeerConnected(this.user.email);
  }

  private async checkConsumedStreams() {
    if (this.consumerWatchdogShouldStandDown()) return;

    const remoteStreamers = [
      ...(this.publisher && !this.isCurrentUser(this.publisher.email) ? [this.publisher] : []),
      ...this.viewers.filter(v => !this.isCurrentUser(v.email)),
    ].filter(u => u.streaming && !this.autoplayBlocked.has(u.email));

    for (const [email] of this.consumerStallCounts) {
      if (!remoteStreamers.some(u => u.email === email)) {
        this.consumerStallCounts.delete(email);
        this.consumerLastProgress.delete(email);
      }
    }

    for (const user of remoteStreamers) {
      // A paused element decodes nothing. That is the phone's media policy, not
      // a broken consumer, and rebuilding one will not start playback.
      const video = this.getVideo(user.email);
      const stats = video?.paused ? null : await this.mediasoup.getInboundVideoStats(user.email);
      // No consumer yet (or it just closed) is not a stall -- the newConsumer
      // path or peer-rejoining will bring it back.
      if (!stats) {
        this.consumerStallCounts.delete(user.email);
        this.consumerLastProgress.delete(user.email);
        continue;
      }

      const previous = this.consumerLastProgress.get(user.email);
      this.consumerLastProgress.set(user.email, stats.progress);
      if (previous === undefined || stats.progress > previous) {
        this.consumerStallCounts.delete(user.email);
        this.consumerRecoveries = 0;
        continue;
      }

      const stalls = (this.consumerStallCounts.get(user.email) ?? 0) + 1;
      this.consumerStallCounts.set(user.email, stalls);
      console.warn('[consumer-watchdog] no progress from', user.email,
        `| checks: ${stalls}/${StreamComponent.CONSUMER_STALL_CHECKS}`,
        `| ${stats.metric}:`, stats.progress);
      if (stalls < StreamComponent.CONSUMER_STALL_CHECKS) continue;

      if (this.consumerRecoveries >= StreamComponent.CONSUMER_MAX_RECOVERIES) {
        console.error('[consumer-watchdog] giving up on', user.email,
          'after', this.consumerRecoveries, 'rebuilds - stream needs a manual restart');
        this.stopConsumerWatchdog();
        return;
      }

      this.consumerRecoveries++;
      console.warn('[consumer-watchdog] rebuilding consumers, attempt', this.consumerRecoveries);
      this.consumerStallCounts.clear();
      this.consumerLastProgress.clear();
      await this.reconnectAsConsumer(this.user.email);
      return;
    }
  }

  private scheduleDisconnectRecovery(peerId: string) {
    if (this.disconnectRecoveryTimers.has(peerId)) return;
    const timer = setTimeout(async () => {
      this.disconnectRecoveryTimers.delete(peerId);
      if (this.mediasoup.isPeerConnected(peerId)) {
        console.warn('[stream] protoo reconnected on its own for', peerId, '- no restart needed');
        return;
      }
      if (!this.hasJoined() || document.visibilityState === 'hidden') return;
      console.warn('[stream] protoo still down for', peerId, '- restarting streams');
      await this.restartStreams();
    }, StreamComponent.DISCONNECT_GRACE_MS);
    this.disconnectRecoveryTimers.set(peerId, timer);
  }

  private cancelDisconnectRecovery(peerId: string) {
    const timer = this.disconnectRecoveryTimers.get(peerId);
    if (!timer) return;
    clearTimeout(timer);
    this.disconnectRecoveryTimers.delete(peerId);
  }

  private clearDisconnectRecovery() {
    this.disconnectRecoveryTimers.forEach(t => clearTimeout(t));
    this.disconnectRecoveryTimers.clear();
  }

  private async reconnectAsConsumer(peerId: string) {
    if (this.isReconnecting) {
      console.warn('[reconnect] already in progress, skipping duplicate call');
      return;
    }
    this.isReconnecting = true;
    const remoteUsers = [
      ...(this.publisher && !this.isCurrentUser(this.publisher.email) ? [this.publisher] : []),
      ...this.viewers.filter(v => !this.isCurrentUser(v.email)),
    ];
    console.warn('[reconnect] starting | peerId:', peerId,
      '| publisher:', this.publisher?.email,
      '| viewers:', this.viewers.map(v => v.email),
      '| isPeerReady:', this.mediasoup.isPeerReady(peerId),
      '| remote streaming users:', remoteUsers.filter(u => u.streaming).map(u => u.email));
    try {
      // Null all remote srcObjects — so isStreaming() returns false after reconnect,
      // letting processConsumer → consumeStream attach fresh tracks from the server.
      this.clearRemoteVideoSrcObjects();

      console.warn('[reconnect] waiting 500ms before cleanup...');
      await new Promise(res => setTimeout(res, 500));

      await this.mediasoupInit?.catch(() => undefined);

      console.warn('[reconnect] calling cleanupPeer...');
      this.mediasoup.cleanupPeer(peerId);

      console.warn('[reconnect] calling connectStream...');
      if (peerId === this.user.email) await this.initMediaSoup();
      else await this.mediasoup.connectStream(peerId, this.room, true);

      console.warn('[reconnect] connectStream resolved — server should now send newConsumer events for each active producer');
    } catch (err) {
      console.error('[reconnect] FAILED:', err);
    } finally {
      this.isReconnecting = false;
      console.warn('[reconnect] done');
    }
  }

  private async updateAspectRatio(aspect?: string) {
    let stream = this.stream;
    if (!stream) return;
    let videoTrack = stream.getVideoTracks()[0];
    // Applying constraints to a track whose source has ended throws
    // OverconstrainedError ("not connected to any source") — skip it.
    if (!videoTrack || videoTrack.readyState !== 'live') return;
    if (!this.maxWidth || !this.maxHeight) {
      let { width, height } = videoTrack.getSettings() || {
        width: 1280,
        height: 720,
      };
      this.maxWidth = width as number;
      this.maxHeight = height as number;
    }

    this.aspect = aspect ?? (this.aspect || this.getOrientation());
    let constraints = this.getVideoConstraints(this.aspect);
    try {
      await videoTrack.applyConstraints(constraints);
    } catch (err) {
      console.warn('[updateAspectRatio] applyConstraints failed:', err);
    }
  }

  private getVideoConstraints(aspect: string): MediaTrackConstraints {
    const base = 720; //Math.min(this.maxWidth, this.maxHeight); // this.isLandscape ? Math.max(this.maxWidth, this.maxHeight) : Math.min(this.maxWidth, this.maxHeight);
    let constraints = {
      aspectRatio: 1,
      width: base,
      height: base,
    };

    switch (aspect) {
      // case '9:16':
      //   constraints = {
      //     aspectRatio: 9 / 16,
      //     width: 720,
      //     height: 1280
      //   };
      // break;
      case "16:9":
        constraints = {
          aspectRatio: 16 / 9,
          width: 1280,
          height: 720,
        };
    }

    this.width = constraints.width;
    this.height = constraints.height;
    return constraints;
  }

  playVideo(video: HTMLVideoElement, stream: MediaStream, producerId: string) {
    return new Promise((resolve, reject) => {
      video.srcObject = stream;
      video.playsInline = true;

      if (!this.isCurrentUser(producerId)) {
        video.onstalled = () => {
          console.warn(`Video stalled for ${producerId}, re-consuming`);
          this.consumeStream(producerId);
        };
      }

      video.onloadedmetadata = () => {
        video.muted = this.isCurrentUser(producerId);
        // video.volume = 1;
        const playPromise = video.play();

        console.log('----> playPromise :>>', playPromise);
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              resolve("Ok");
            })
            .catch((error: any) => {
              console.warn("Autoplay blocked, waiting for user interaction:", error);
              this.autoplayBlocked.add(producerId);
              resolve("Ok (blocked)");
            });
        }
      };
      video.onerror = (err) => {
        console.log("err :>> ", err);
        reject(err.toString());
      };
    });
  }

  async resumeVideo(producerId: string) {
    const video = this.getVideo(producerId);
    if (!video) return;
    try {
      await video.play();
      this.autoplayBlocked.delete(producerId);
    } catch (err) {
      console.error("Failed to resume video:", err);
    }
  }

  async switchCamera() {
    if (this.isSwitchingCamera || !this.stream) return;
    this.isSwitchingCamera = true;

    const recorder = this.mediaRecorder;
    if (recorder?.state === 'recording') recorder.pause();

    this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    const videoConstraints: MediaTrackConstraints = {
      facingMode: this.currentFacingMode,
      width: { ideal: this.width },
      height: { ideal: this.height },
    };

    try {
      const oldVideoTrack = this.stream.getVideoTracks()[0];
      if (oldVideoTrack) {
        this.stream.removeTrack(oldVideoTrack);
        oldVideoTrack.stop();
      }

      const newStream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!newVideoTrack) throw new Error('No video track in new stream');

      await this.mediasoup.replaceVideoTrack(this.user.email, newVideoTrack);

      this.stream.addTrack(newVideoTrack);
      // The camera was on to reach here, so keep the new track live (a false publisher.camera
      // read mid-switch would leave a black frame).
      newVideoTrack.enabled = this.publisher?.camera !== false;

      const publisherVideoEl = this.publisherVideo?.nativeElement;
      if (publisherVideoEl) {
        publisherVideoEl.srcObject = null;
        publisherVideoEl.srcObject = this.stream;
        // Mobile Safari/Chrome need an explicit play() after swapping srcObject or the
        // preview stays black even though the track is live.
        await publisherVideoEl.play().catch(() => {});
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { mode: this.currentFacingMode },
        queryParamsHandling: 'merge',
      });
    } catch (err) {
      console.error('[switchCamera] replaceTrack failed, falling back to full restart:', err);
      this.stopLocalPublisher(false);
      await this.startMainStream();
      if (this.recording) this.applyRecordingState(this.recording);
    } finally {
      this.isSwitchingCamera = false;
      if (recorder && recorder === this.mediaRecorder && recorder.state === 'paused') recorder.resume();
    }
  }

  isVideoPlaying(video: HTMLVideoElement): boolean {
    return (
      video.currentTime > 0 &&
      !video.ended &&
      video.readyState > HTMLMediaElement.HAVE_CURRENT_DATA
    );
  }

  getVideo(peerId: string) {
    let remoteVideo = this.isPublisher(peerId)
      ? this.publisherVideo?.nativeElement
      : undefined;
    if (!remoteVideo) {
      let viewerIndex = this.viewers.findIndex((v) => v.email == peerId);
      if (viewerIndex == -1) return;
      remoteVideo = this.viewerVideos.toArray()[viewerIndex]?.nativeElement;
    }
    return remoteVideo;
  }

  async processConsumer(user: SocketUser) {
    if (!this.hasJoined() || this.isCurrentUser(user)) return;
    await new Promise((res) => setTimeout(res, 100));
    const streaming = this.isStreaming(user);
    console.log('[processConsumer]', user.email, '| user.streaming:', user.streaming, '| isStreaming:', streaming);
    if (user.streaming && !streaming)
      this.consumeStream(user.email);
  }

  toggleMic(user: SocketUser) {
    if (!this.isCurrentUser(user)) return;
    user.muted = !user.muted;
    let localStream =
      this.isPublisher(user.email) && this.isPublisher() ? this.stream : null;
    if (!localStream && this.isCurrentUser(user.email) && this.isViewer())
      localStream = this.viewerStream;
    if (localStream && user)
      localStream?.getAudioTracks().forEach((track) => {
        track.enabled = !user.muted;
      });

    let micParams = this.isPublisher()
      ? this.publisherParams
      : this.viewerParams;
    if (user.muted && micParams.includes("mic"))
      micParams.splice(micParams.indexOf("mic"), 1);
    else if (!user.muted && !micParams.includes("mic")) micParams.push("mic");
    this.ws.streamEvent("mute", { userId: user.id, muted: user.muted });
  }

  toggleCamera(user: SocketUser) {
    if (!this.isCurrentUser(user)) return;
    user.camera = !user.camera;

    let localStream =
      this.isPublisher(user.email) && this.isPublisher() ? this.stream : null;
    if (!localStream && this.isCurrentUser(user.email) && this.isViewer())
      localStream = this.viewerStream;
    if (localStream && user)
      localStream?.getVideoTracks().forEach((track) => {
        track.enabled = !!user.camera;
      });

    let cameraParams = this.isPublisher()
      ? this.publisherParams
      : this.viewerParams;
    if (!user.camera && cameraParams.includes("camera"))
      cameraParams.splice(cameraParams.indexOf("camera"), 1);
    else if (user.camera && !cameraParams.includes("camera"))
      cameraParams.push("camera");

    this.ws.streamEvent("camera", { userId: user.id, camera: user.camera });
  }

  async toggleGeoloc(user: SocketUser) {
    if (!this.isCurrentUser(user)) return;
    user.geoloc = !user.geoloc;
    if (user.geoloc) {
      let status = await this.gps.checkGeolocationStatus();
      if (status == "denied") {
        this.pop.error(this.translate.instant("stream.geoloc.deactivated"));
        user.geoloc = false;
        return;
      }
      try {
        await this.initGPS();
      } catch {
        user.geoloc = false;
        return;
      }
    } else {
      this.stopGeoloc();
    }

    this.ws.streamEvent("geoloc", { userId: user.id, geoloc: user.geoloc });
  }

  stopGeoloc() {
    this.gpsDestroy$.next();
    this.gpsDestroy$.complete();
    this.gps.destroy();
  }

  //-------------- Recording functions -------------------------------------------
  // A viewer drives the recording; the publisher's client captures, off its own
  // camera track rather than the mediasoup copy. A NUC has no client of its own,
  // so the viewer who started the recording captures the consumed stream.
  canToggleRecording(): boolean {
    if (!this.isViewer()) return false;
    if (this.isBroadcasterMode()) return this.trackerStreamLive || !!this.recording;
    return !!this.publisher?.streaming;
  }

  isRecorder(): boolean {
    if (!this.recording) return this.isPublisher();
    if (this.recording.recorder) return this.recording.recorder === this.user?.id;
    if (this.recording.source === "broadcaster" || (!this.publisher && this.broadcaster)) {
      return this.isBroadcasterMode() && this.recording.requestedBy === this.user?.id;
    }
    return this.isPublisher();
  }

  private resumeRecording() {
    if (!this.recording || !this.isRecorder() || this.isRecording) return;
    void this.startRecorder();
  }

  canStopRecording(): boolean {
    return !!this.recording && this.recording.requestedBy === this.user?.id;
  }

  toggleRecording() {
    if (!this.canToggleRecording()) return;
    if (!this.recording) {
      this.ws.streamEvent("recording-start");
    } else if (this.canStopRecording()) {
      this.ws.streamEvent("recording-stop");
    }
  }

  // Room state is authoritative: the server stamps who asked and when.
  private applyRecordingState(next?: StreamRecording | null, serverNow?: number) {
    const previous = this.recording;
    this.recording = next ?? null;

    if (!this.recording) {
      if (previous) this.stopTimer();
      this.closeSegment();
      this.finishActiveUpload();
      return;
    }

    if (this.recording.startedAt !== previous?.startedAt) {
      // Without the server clock, a late joiner falls back to the server stamp
      // and wears the clock skew.
      const serverElapsed = Date.now() - this.recording.startedAt;
      this.recordingAnchor = typeof serverNow === "number"
        ? Date.now() - Math.max(0, serverNow - this.recording.startedAt)
        : serverElapsed > 5000 ? this.recording.startedAt : Date.now();
      this.startTimer();
    }

    if (!this.isRecorder()) {
      if (this.activeUpload) {
        this.closeSegment();
        this.activeUpload = null;
      }
      return;
    }
    if (!this.isRecording) void this.startRecorder();
  }

  private async startRecorder() {
    if (this.openingRecorder || this.isRecording || !this.recording || this.leavingRecording || !this.hasJoined()) return;
    this.openingRecorder = true;
    try {
      await this.recorderStopped;
      const target = this.activeUpload?.uploadId
        ?? (await this.recordingService.findOpen(this.user.email, this.event?.id).catch(() => undefined))?.uploadId;
      const continuation = target ? await this.prepareContinuation(target) : null;
      if (!this.recording || !this.isRecorder() || this.isRecording) return;
      this.openSegment(continuation ?? undefined);
    } finally {
      this.openingRecorder = false;
    }
  }

  private async prepareContinuation(uploadId: string): Promise<RecordingContinuation | null> {
    await this.recordingService.pause(uploadId);
    const local = await this.recordingService.get(uploadId).catch(() => undefined);
    if (!local || local.owner !== this.user.email) return null;
    if (local.started) {
      try {
        const status = await firstValueFrom(this.attachmentService.getVideoRecording(uploadId, local.db));
        if (status.status !== "open" && status.status !== "closing") return null;
      } catch (err) {
        if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500) return null;
      }
    }
    await this.recordingService.reopen(uploadId);
    const offset = local.lastTimecodeAt
      ? local.lastTimecode + Math.max(1, Date.now() - local.lastTimecodeAt)
      : Math.max(Date.now() - Date.parse(local.meta.createdDate), local.lastTimecode + 1);
    return {
      uploadId,
      createdDate: local.meta.createdDate,
      thumbnail: local.thumbnail,
      eventId: local.meta.event,
      offset,
      fresh: local.written === 0,
    };
  }

  private openSegment(continuation?: RecordingContinuation) {
    const nucTracker = this.isBroadcasterMode() ? this.broadcasterTracker() : undefined;
    const source = nucTracker ? this.mediasoup.getConsumerStream(nucTracker.name) : this.stream;
    if (!source?.getTracks().some((t) => t.readyState === "live")) {
      console.warn("[recording] no live local stream to record");
      return;
    }

    this.recordedBytes = 0;
    const recordSource = nucTracker ? { stream: new MediaStream(source.getVideoTracks()), stop: () => {} } : this.createRecordingSource(source);
    const options = { mimeType: nucTracker ? "video/webm;codecs=vp8" : "video/webm;codecs=vp8,opus" };
    try {
      this.mediaRecorder = new MediaRecorder(recordSource.stream, options);
    } catch (err) {
      console.warn("[recording] falling back to default MediaRecorder options:", err);
      this.mediaRecorder = new MediaRecorder(recordSource.stream);
    }
    const recorder = this.mediaRecorder;
    const webm = this.recordsWebm(recorder);
    if (continuation && !webm) {
      void this.recordingService.end(continuation.uploadId);
      continuation = undefined;
    }

    const uploadId = continuation?.uploadId ?? crypto.randomUUID();
    const createdDate = continuation?.createdDate ?? new Date().toISOString();
    const thumbnail = continuation?.thumbnail
      ?? this.grabVideoThumbnail(nucTracker ? this.trackerVideo?.nativeElement : this.publisherVideo?.nativeElement)
      ?? this.fallbackVideoThumbnail();
    const eventId = continuation ? continuation.eventId : this.event?.id;
    const stitcher = webm ? new WebmStitcher(continuation ? continuation.offset : null, !!continuation?.fresh) : undefined;
    this.activeUpload = { uploadId, createdDate, thumbnail, eventId, source: nucTracker };

    if (!continuation) {
      void this.registerRecording(uploadId, createdDate, thumbnail, eventId, nucTracker).then((ok) => {
        if (ok || this.activeUpload?.uploadId !== uploadId) return;
        this.activeUpload = null;
        this.closeSegment();
      });
    }

    let stopped!: () => void;
    this.recorderStopped = new Promise<void>((resolve) => (stopped = resolve));
    recorder.ondataavailable = (e) => {
      if (!e.data.size) return;
      this.recordedBytes += e.data.size;
      this.recordingService.append(uploadId, e.data, stitcher);
    };
    recorder.onstop = () => {
      recordSource.stop();
      stopped();
      if (this.mediaRecorder === recorder) this.handleUnexpectedRecorderStop();
    };

    recorder.start(StreamComponent.RECORDER_TIMESLICE_MS);
    this.isRecording = true;
  }

  private recordsWebm(recorder: MediaRecorder): boolean {
    if (recorder.mimeType) return recorder.mimeType.includes("webm");
    return typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported("video/webm");
  }

  private finishActiveUpload() {
    const active = this.activeUpload;
    this.activeUpload = null;
    if (!active) return;
    this.recordingService.markClosing(active.uploadId);
    void this.recorderStopped
      .then(() => this.recordingService.end(active.uploadId))
      .catch((err) => console.error("[recording] could not close the upload:", err));
    this.showRecordingUpload(active.uploadId, active.createdDate, active.thumbnail, active.eventId, active.source);
  }

  private async registerRecording(uploadId: string, createdDate: string, thumbnail: string, eventId: string | undefined, source?: { id: string; name: string }): Promise<boolean> {
    try {
      const tracker = source ?? await this.resolveUserTracker(this.user.email);
      await this.recordingService.begin(uploadId, this.user.email, {
        tracker: tracker.id,
        trackerName: tracker.name,
        event: eventId,
        camera: "carried",
        createdDate,
        source: "capture",
        fromHse: true,
      }, thumbnail);
      return true;
    } catch (err) {
      console.error("[recording] could not register the recording:", err);
      this.pop.error(toError(err));
      return false;
    }
  }

  private grabVideoThumbnail(video?: HTMLVideoElement): string | undefined {
    if (!video?.videoWidth) return undefined;
    const canvas = document.createElement("canvas");
    canvas.width = 160;
    canvas.height = 90;
    canvas.getContext("2d")!.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.8);
  }

  private showRecordingUpload(uploadId: string, createdDate: string, thumbnail: string | undefined, eventId: string | undefined, source?: { id: string; name: string }) {
    const placeholder: Capture = {
      ...this.getPlaceholder({ type: "video", createdDate, loading: false, trackerName: source?.name }),
      id: uploadId,
      thumbnail,
      event: eventId,
      uploading: true,
      uploadStage: 1,
      uploadProgress: 0,
    };
    this.pendingVideos.set(uploadId, {
      url: "", thumbnail: thumbnail ?? "", file: new File([], `${uploadId}.webm`), placeholder,
      segment: true, tracker: source, createdDate, eventId, recording: true,
      state$: new BehaviorSubject<{ uploading: boolean; videoSrc?: string } | null>({ uploading: true }),
    });
    const targetEvent = this.events.find(e => e.id === eventId);
    if (targetEvent) {
      targetEvent.captures = this.buildCaptures({ ...targetEvent, captures: [...(targetEvent.captures ?? []), placeholder] });
      if (this.event?.id === targetEvent.id) this.captures = targetEvent.captures;
      this.eventsData.data = [...this.events];
    } else {
      this.captures = this.sortCaptures([...this.captures.filter(c => c.id !== uploadId), placeholder]);
    }
    this.broadcastVideoUpload(uploadId, true);
    this.startVideoUploadHeartbeat();
  }

  private onRecordingProgress(progress: RecordingProgress) {
    const pending = this.pendingVideos.get(progress.uploadId);
    if (!pending?.recording || !progress.stopped || !progress.written) return;
    pending.placeholder.uploadProgress = Math.min(100, Math.round(progress.uploaded / progress.written * 100));
    this.broadcastVideoUpload(progress.uploadId);
  }

  private onRecordingDone(done: RecordingDone) {
    this.pendingVideos.get(done.uploadId)?.state$.next({ uploading: false, videoSrc: done.video.videoSrc ?? done.video.url });
    if (done.meta.trackerName) this.linkTrackerToEvent({ id: done.meta.tracker, name: done.meta.trackerName }, done.meta.event);
    void this.handleVideoUploaded(done.uploadId, done.attachment, done.video);
  }

  private onRecordingFull(uploadId: string) {
    if (this.activeUpload?.uploadId !== uploadId) return;
    this.closeSegment();
    this.finishActiveUpload();
    this.resumeRecording();
  }

  private onRecordingDropped(dropped: { uploadId: string; reason: string }) {
    if (dropped.reason !== "empty") console.warn(`[recording] ${dropped.uploadId} closed by the server (${dropped.reason})`);
    if (this.activeUpload?.uploadId === dropped.uploadId) {
      this.activeUpload = null;
      this.closeSegment();
      if (dropped.reason !== "forbidden" && dropped.reason !== "finalized") this.resumeRecording();
    }
    const pending = this.pendingVideos.get(dropped.uploadId);
    if (!pending?.recording) return;
    this.broadcastVideoUploadEnd(dropped.uploadId);
    pending.placeholder.uploading = false;
    if (!pending.id) this.removeVideoPlaceholder(dropped.uploadId);
    this.pendingVideos.delete(dropped.uploadId);
  }

  private createRecordingSource(source: MediaStream): { stream: MediaStream; stop: () => void } {
    const canvas = document.createElement("canvas");
    const ctx = typeof canvas.captureStream === "function" ? canvas.getContext("2d") : null;
    if (!ctx) return { stream: source, stop: () => {} };

    const preview = this.publisherVideo?.nativeElement;
    const settings = source.getVideoTracks()[0]?.getSettings();
    canvas.width = preview?.videoWidth || settings?.width || 1280;
    canvas.height = preview?.videoHeight || settings?.height || 720;

    let frame = 0;
    const draw = () => {
      const video = this.publisherVideo?.nativeElement;
      if (video?.videoWidth && !this.isSwitchingCamera) {
        const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
        const w = video.videoWidth * scale;
        const h = video.videoHeight * scale;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      }
      frame = requestAnimationFrame(draw);
    };
    draw();

    const stream = canvas.captureStream(30);
    source.getAudioTracks().forEach((track) => stream.addTrack(track));
    return {
      stream,
      stop: () => {
        cancelAnimationFrame(frame);
        stream.getVideoTracks().forEach((track) => track.stop());
      },
    };
  }

  private handleUnexpectedRecorderStop() {
    this.mediaRecorder = null;
    this.isRecording = false;
    if (!this.recording || !this.isRecorder()) return;
    console.warn("[recording] recorder stopped on its own, continuing the upload");
    void this.startRecorder();
  }

  private fallbackVideoThumbnail(): string {
    const canvas = document.createElement("canvas");
    canvas.width = 160;
    canvas.height = 90;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#2b2b2b";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(68, 30);
      ctx.lineTo(68, 60);
      ctx.lineTo(96, 45);
      ctx.closePath();
      ctx.fill();
    }
    return canvas.toDataURL("image/jpeg", 0.8);
  }

  // Video tiles render as <img>, which a webm blob URL cannot fill, and the
  // upload route wants the same frame as base64 under a 1 MB field limit.
  private async makeVideoThumbnail(blob: Blob, timeoutMs = StreamComponent.THUMBNAIL_TIMEOUT_MS): Promise<string> {
    const url = URL.createObjectURL(blob);
    const video = document.createElement("video");
    try {
      video.src = url;
      video.muted = true;
      // An unreadable segment fires neither event, so don't wait for ever.
      await Promise.race([
        new Promise<void>((resolve, reject) => {
          video.onloadedmetadata = () => resolve();
          video.onerror = () => reject(new Error("could not read the segment"));
        }).then(() => this.seekVideoTo(video, 0)),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("timed out reading the segment")),
            timeoutMs,
          ),
        ),
      ]);
      const canvas = document.createElement("canvas");
      canvas.width = 160;
      canvas.height = 90;
      canvas.getContext("2d")!.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", 0.8);
    } finally {
      video.remove();
      URL.revokeObjectURL(url);
    }
  }

  private closeSegment() {
    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    this.isRecording = false;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch (err) {
        console.warn("[recording] stop failed:", err);
      }
    }
  }

  startTimer() {
    this.stopTimer();
    this.tickTimer();
    this.recordingInterval = setInterval(() => this.tickTimer(), 1000);
  }

  private tickTimer() {
    const elapsed = Math.max(0, Math.floor((Date.now() - this.recordingAnchor) / 1000));
    const minutes = Math.floor(elapsed / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (elapsed % 60).toString().padStart(2, "0");
    this.recordingTime = `${minutes}:${seconds}`;
  }

  stopTimer() {
    if (this.recordingInterval) clearInterval(this.recordingInterval);
    this.recordingInterval = null;
    this.recordingTime = "00:00";
  }

  //----------------- Capture functions ----------------------------------------
  takePhoto(mode: CaptureMode = "manual") {
    if (this.isBroadcasterMode()) {
      if (this.hasJoined() && this.trackerStreamLive) this.captureBroadcasterPhoto();
      return;
    }
    if (!this.hasJoined() || !this.isStreaming(this.publisher)) return;
    const createdDate = new Date().toISOString();

    if (this.isPublisher()) {
      void this.capturePublisherPhoto(createdDate, mode);
      return;
    }

    this.addRequestedCapture(createdDate);
    this.ws.streamEvent("capture-request", { createdDate, mode });
  }

  toggleAutoCapture() {
    if (this.isAutoCapturing) {
      clearInterval(this.autoCaptureInterval);
      this.isAutoCapturing = false;
    } else {
      this.isAutoCapturing = true;
      this.autoCaptureInterval = setInterval(() => this.takePhoto("auto"), 5000);
    }
  }

  private addRequestedCapture(createdDate: string) {
    const placeholder = this.getPlaceholder({ type: "photo", createdDate });
    const target = this.events.find(e => e.id === this.event?.id);
    if (target) target.captures = [...(target.captures ?? []), placeholder];
    this.captures = this.sortCaptures([...this.captures, placeholder]);
    setTimeout(() => this.dropRequestedCapture(placeholder.id), StreamComponent.CAPTURE_REQUEST_TIMEOUT_MS);
  }

  private dropRequestedCapture(placeholderId: string) {
    const target = this.events.find(e => e.captures?.some(c => c.id === placeholderId));
    if (target) {
      target.captures = target.captures!.filter(c => c.id !== placeholderId);
      if (this.event?.id === target.id) this.captures = this.buildCaptures(target);
    }
    this.captures = this.captures.filter(c => c.id !== placeholderId);
  }

  private async capturePublisherPhoto(createdDate: string, mode: CaptureMode) {
    try {
      const base64 = await this.grabCameraPhoto(mode);
      if (!base64) {
        console.warn("[capture] no live camera to capture from");
        return;
      }
      this.postAttachment(base64, "photo", "carried", undefined, createdDate, false, false);
    } catch (err) {
      console.error("[capture] failed:", err);
    }
  }

  private captureBroadcasterPhoto() {
    const video = this.trackerVideo?.nativeElement;
    const source = this.broadcasterTracker();
    if (!video?.videoWidth || !source) return;
    const base64 = this.bitmapToJpeg(video, video.videoWidth, video.videoHeight);
    this.postAttachment(base64, "photo", "carried", undefined, new Date().toISOString(), false, true, source);
  }

  private async grabCameraPhoto(mode: CaptureMode): Promise<string | null> {
    const track = this.stream?.getVideoTracks().find(t => t.readyState === "live");
    if (!track) return null;

    const ImageCaptureCtor = (window as any).ImageCapture;
    if (mode === "manual" && ImageCaptureCtor) {
      try {
        const blob: Blob = await new ImageCaptureCtor(track).takePhoto();
        const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
        try {
          return this.bitmapToJpeg(bitmap, bitmap.width, bitmap.height);
        } finally {
          bitmap.close();
        }
      } catch (err) {
        console.warn("[capture] takePhoto unavailable, using a video frame:", err);
      }
    }

    const video = this.publisherVideo.nativeElement;
    if (!video.videoWidth) return null;
    return this.bitmapToJpeg(video, video.videoWidth, video.videoHeight);
  }

  private bitmapToJpeg(source: CanvasImageSource, width: number, height: number): string {
    const scale = Math.min(1, StreamComponent.MAX_CAPTURE_EDGE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas.getContext("2d")!.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.9);
  }

  toggleDetections() {
    this.detectionsActive = !this.detectionsActive;
    this.hasInitDetections = this.detectionsActive;
    this.ws.streamEvent("detect", { detect: this.detectionsActive });
  }

  isHiddenForPublisher(item: Capture, event?: _Event): boolean {
    const isOwner = event ? event.user === this.user?.id : this.isPublisher();
    return isOwner && item.type !== 'video' && !item.loading && !this.getFirstLabel(item);
  }

  getFirstLabel(item: Capture): StatusLabel | undefined {
    return item.statuses?.find(status => status.labelProject == this.labelProject?.id)?.labels?.[0];
  }

  downloadCapture(item: any, index: number) {
    const filename = item.type === "photo"
      ? `photo_${index + 1}_${item.createdDate}.jpg`
      : `video_${index + 1}_${item.createdDate}.mp4`;

    fetch(item.url)
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  }

  deleteCapture(index: number) {
    const item = this.captures[index];

    if (item?.type === "video") {
      URL.revokeObjectURL(item.url);
    }

    this.captures = this.captures.filter((_, i) => i !== index);

    if (!item?.id) return;

    if (this.event?.images) {
      this.event.images = this.event.images.filter(img => img.id !== item.id);
    }
    if (this.event?.message?.['attachments'] && item.attachment) {
      this.event.message['attachments'] = this.event.message['attachments'].filter((a: string) => a !== item.attachment);
    }

    this.ws.streamEvent("image-deleted", { imageId: item.id, attachmentId: item.attachment, eventId: this.event?.id });

    const associatedRisk = this.risks.find(r => r.payload?.['attachment'] === item.attachment);
    console.log('----> associatedRisk :>>', associatedRisk);
    if (associatedRisk) {
      this.risks = this.risks.filter(r => r.id !== associatedRisk.id);
      this.metricService.removeMetric(associatedRisk.id).pipe(takeUntil(this.destroy$)).subscribe();
      this.eventService.removeEvents({ attachment: item.attachment! }).pipe(takeUntil(this.destroy$)).subscribe();
    }

    if (item.type === 'video' && item.attachment) {
      this.videoService.deleteVideo(item.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.eventsData.data = this.events;
          this.pop.toast(this.translate.instant(`stream.upload.deleted_video`), 'success');
        },
        error: (err) => this.pop.error(toError(err)),
      });
    } else
      this.imageService.deleteImage(item.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.eventsData.data = this.events;
          this.pop.toast(this.translate.instant(`stream.upload.delete`), 'success');
        },
        error: (err) => this.pop.error(toError(err)),
      });
  }

  uploadCapture(capture: any, i: number) {}

  async onFileSelected(files: FileList | Event, type: "photo" | "video" = "photo") {
    let fileList: FileList | null;

    if (files instanceof Event) {
      const target = files.target as HTMLInputElement;
      fileList = target.files;
    } else {
      fileList = files;
    }

    if (!fileList?.length) return;
    this.claimUploaderRole();

    const photoFiles: File[] = [];
    Array.from(fileList).forEach(file => {
      if (!file.type.startsWith('video/')) photoFiles.push(file);
      else if (file.size > StreamComponent.MAX_VIDEO_BYTES) {
        this.pop.error(this.translate.instant('stream.upload.exceeds', { maxSize: StreamComponent.MAX_VIDEO_BYTES / 1024 ** 3 }));
      }
      else this.processVideoFile(file);
    });

    if (!photoFiles.length) return;

    const batchId = crypto.randomUUID();
    // Process and upload in chunks so large selections so no memory/thread flooding
    const CHUNK_SIZE = 10;
    for (let i = 0; i < photoFiles.length; i += CHUNK_SIZE) {
      const chunk = photoFiles.slice(i, i + CHUNK_SIZE);
      const items: { base64: string; createdDate: string }[] = [];

      for (const file of chunk) {
        const item = await this.processImageFile(file, type);
        if (item) items.push(item);
        // Yield to the browser so the UI stays responsive while parsing.
        await new Promise(res => setTimeout(res, 0));
      }

      const isLastChunk = i + CHUNK_SIZE >= photoFiles.length;
      await this.postManyAttachments(items, "carried", isLastChunk, batchId);
    }

    this.attachmentService
      .completeUploadBatch({ batchId, total: photoFiles.length, source: 'upload', event: this.event?.id })
      .pipe(first())
      .subscribe({ error: (err) => console.warn("[upload] batch completion failed:", err) });
  }

  /**
   * Read a single image file, resize it to the target resolution if needed,
   * and resolve to its base64 payload + creation date. Resolves to null if
   * the file cannot be read/decoded.
   */
  private async processImageFile(
    file: File,
    type: "photo" | "video",
  ): Promise<{ base64: string; createdDate: string } | null> {
    const requiredWidth = 1280;
    const requiredHeight = 720;
    const createdDate = await this.tools.getFileCreationDate(file);

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e: any) => {
        const img = new Image();
        img.src = e.target.result as string;

        img.onload = () => {
          let base64: string;
          if (
            type == "photo" &&
            img.width <= requiredWidth &&
            img.height <= requiredHeight
          ) {
            base64 = e.target.result;
          } else {
            const canvas = document.createElement("canvas");
            const ctx: any = canvas.getContext("2d");

            canvas.width = requiredWidth;
            canvas.height = requiredHeight;

            const scale = Math.min(
              requiredWidth / img.width,
              requiredHeight / img.height,
            );
            const newWidth = img.width * scale;
            const newHeight = img.height * scale;
            const offsetX = (requiredWidth - newWidth) / 2;
            const offsetY = (requiredHeight - newHeight) / 2;

            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, requiredWidth, requiredHeight);

            ctx.drawImage(img, offsetX, offsetY, newWidth, newHeight);
            base64 = canvas.toDataURL("image/png");
          }
          console.log('----> createdDate :>>', createdDate);
          resolve({ base64, createdDate });
        };
        img.onerror = () => resolve(null);
      };

      reader.onerror = (error) => {
        console.error("Error loading file:", error);
        resolve(null);
      };
    });
  }

  private async processVideoFile(file: File): Promise<void> {
    this.processingVideos++;
    const url = URL.createObjectURL(file);
    let thumbnail: string;
    try {
      thumbnail = await this.makeVideoThumbnail(file, StreamComponent.FILE_THUMBNAIL_TIMEOUT_MS);
    } catch (err) {
      console.warn("[upload] browser cannot read this video, uploading with a placeholder thumbnail:", file.name, err);
      thumbnail = this.fallbackVideoThumbnail();
    } finally {
      this.processingVideos--;
    }

    const placeholderId = 'upload-' + crypto.randomUUID();
    const createdDate = this.tools.parseDateFromFilename(file.name) ?? new Date().toISOString();
    const placeholder: Capture = {
      id: placeholderId,
      type: 'video',
      url,
      thumbnail,
      loading: false,
      createdDate,
      trackerName: this.user.email,
      projectName: this.project?.name ?? '',
    };
    this.enqueueVideoUpload(placeholder, file, thumbnail);
    if (this.event) {
      if (!this.event.captures) this.event.captures = [];
      this.event.captures.push(placeholder);
      this.eventsData.data = [...this.events];
    }
    this.captures = this.sortCaptures([...this.captures.filter(c => c.id !== placeholderId), placeholder]);
  }

  private enqueueVideoUpload(placeholder: Capture, file: File, thumbnail: string, segment = false, tracker?: { id: string; name: string }): void {
    placeholder.uploading = true;
    placeholder.uploadProgress = 0;
    placeholder.uploadStage = 0;
    this.pendingVideos.set(placeholder.id, {
      url: placeholder.url, thumbnail, file, placeholder, segment, tracker,
      createdDate: placeholder.createdDate,
      eventId: this.event?.id,
      state$: new BehaviorSubject<{ uploading: boolean; videoSrc?: string } | null>(null),
    });
    this.videoUploadQueue.push(placeholder.id);
    queueMicrotask(() => this.startQueuedVideoUploads());
    this.broadcastVideoUpload(placeholder.id, true);
    this.startVideoUploadHeartbeat();
  }

  private broadcastVideoUpload(placeholderId: string, force = false): void {
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending?.eventId) return;
    const { placeholder } = pending;
    const last = this.videoUploadLastBroadcast.get(placeholderId);
    const stageChanged = last?.stage !== placeholder.uploadStage;
    if (!force && !stageChanged && last?.progress === placeholder.uploadProgress) return;
    this.videoUploadLastBroadcast.set(placeholderId, { at: Date.now(), stage: placeholder.uploadStage, progress: placeholder.uploadProgress });
    this.ws.streamEvent("video-upload", {
      event: pending.eventId,
      uploadId: placeholderId,
      createdDate: pending.createdDate,
      stage: placeholder.uploadStage,
      progress: placeholder.uploadProgress ?? 0,
      thumbnail: force || stageChanged ? pending.thumbnail : undefined,
    });
  }

  private broadcastVideoUploadEnd(placeholderId: string): void {
    const pending = this.pendingVideos.get(placeholderId);
    this.videoUploadLastBroadcast.delete(placeholderId);
    if (!pending?.eventId) return;
    this.ws.streamEvent("video-upload", { event: pending.eventId, uploadId: placeholderId, done: true });
  }

  private startVideoUploadHeartbeat(): void {
    if (this.videoUploadHeartbeat) return;
    this.videoUploadHeartbeat = setInterval(() => {
      let active = false;
      this.pendingVideos.forEach((pending, id) => {
        if (!pending.placeholder.uploading) return;
        active = true;
        this.broadcastVideoUpload(id, true);
      });
      if (!active) this.stopVideoUploadHeartbeat();
    }, StreamComponent.VIDEO_UPLOAD_HEARTBEAT_MS);
  }

  private stopVideoUploadHeartbeat(): void {
    if (this.videoUploadHeartbeat) clearInterval(this.videoUploadHeartbeat);
    this.videoUploadHeartbeat = null;
  }

  private applyRemoteVideoUpload(msg: {
    eventId: string; uploadId: string; createdDate?: string; stage?: 0 | 1 | 2;
    progress?: number; thumbnail?: string; done?: boolean;
  }): void {
    if (!msg?.uploadId || this.pendingVideos.has(msg.uploadId)) return;
    const target = this.events.find(e => e.id === msg.eventId);
    let remote = this.remoteVideoUploads.get(msg.uploadId);

    if (msg.done) {
      if (remote) this.removeRemoteVideoUpload(msg.uploadId);
      return;
    }
    if (!target || target.videos?.some(v => v.uploadId === msg.uploadId)) return;

    if (!remote) {
      if (!msg.createdDate) return;
      const placeholder: Capture = {
        id: msg.uploadId,
        type: 'video',
        url: '',
        event: msg.eventId,
        createdDate: msg.createdDate,
        loading: false,
        uploading: true,
      };
      remote = { placeholder, eventId: msg.eventId };
      this.remoteVideoUploads.set(msg.uploadId, remote);
      target.captures = this.buildCaptures({ ...target, captures: [...(target.captures ?? []), placeholder] });
      if (this.event?.id === target.id) this.captures = target.captures;
      this.eventsData.data = [...this.events];
    }

    const { placeholder } = remote;
    if (msg.thumbnail) placeholder.thumbnail = msg.thumbnail;
    placeholder.uploadStage = msg.stage ?? 0;
    placeholder.uploadProgress = msg.progress ?? 0;

    clearTimeout(remote.staleTimer);
    remote.staleTimer = setTimeout(
      () => this.removeRemoteVideoUpload(msg.uploadId),
      StreamComponent.REMOTE_VIDEO_UPLOAD_STALE_MS,
    );
  }

  private removeRemoteVideoUpload(uploadId: string): void {
    const remote = this.remoteVideoUploads.get(uploadId);
    if (!remote) return;
    clearTimeout(remote.staleTimer);
    this.remoteVideoUploads.delete(uploadId);
    remote.placeholder.uploading = false;
    const target = this.events.find(e => e.id === remote.eventId);
    if (!target) return;
    target.captures = this.buildCaptures({
      ...target,
      captures: (target.captures ?? []).filter(c => c.id !== uploadId),
    });
    if (this.event?.id === target.id) this.captures = target.captures;
    this.eventsData.data = [...this.events];
  }

  private startQueuedVideoUploads(): void {
    while (this.activeVideoUploads < StreamComponent.MAX_PARALLEL_VIDEO_UPLOADS && this.videoUploadQueue.length) {
      const placeholderId = this.videoUploadQueue.shift()!;
      if (!this.pendingVideos.has(placeholderId)) continue;
      this.activeVideoUploads++;
      this.uploadVideo(placeholderId);
    }
  }

  private async uploadVideo(placeholderId: string): Promise<void> {
    const release = () => {
      this.activeVideoUploads--;
      this.startQueuedVideoUploads();
    };
    let tracker: { id: string; name: string } | undefined = this.pendingVideos.get(placeholderId)?.tracker;
    try {
      tracker ??= await this.resolveUserTracker(this.user.email);
    } catch {}
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending || !tracker) {
      this.failPendingVideo(placeholderId);
      release();
      return;
    }
    const { placeholder, createdDate } = pending;
    placeholder.uploadStage = 1;
    const uploadId = placeholderId;
    pending.state$.next({ uploading: true });
    this.broadcastVideoUpload(placeholderId);
    const serverProgressSub = this.ws.getVideoUploadProgress().pipe(
      filter(msg => msg?.uploadId === uploadId),
      takeUntil(this.destroy$),
    ).subscribe(({ percent }) => {
      placeholder.uploadStage = 2;
      placeholder.uploadProgress = percent;
      this.broadcastVideoUpload(placeholderId);
    });
    this.attachmentService.postVideoAttachment({
      file: pending.file,
      thumbnail: pending.thumbnail,
      tracker: tracker.id,
      trackerName: tracker.name,
      event: pending.eventId,
      camera: 'carried',
      createdDate,
      source: pending.segment ? 'capture' : 'upload',
      uploadId,
      onProgress: (percent) => {
        if (placeholder.uploadStage !== 1) return;
        if (percent < 100) {
          placeholder.uploadProgress = percent;
        } else {
          placeholder.uploadStage = 2;
          placeholder.uploadProgress = 0;
        }
        this.broadcastVideoUpload(placeholderId);
      },
    }).pipe(takeUntil(this.destroy$), finalize(() => {
      serverProgressSub.unsubscribe();
      release();
    })).subscribe({
      next: (result) => {
        pending.state$.next({ uploading: false, videoSrc: result.video.videoSrc ?? result.video.url });
        if (!pending.segment) this.pop.success(this.translate.instant('stream.upload.video_success'));
        this.linkTrackerToEvent(tracker, pending.eventId);
        this.handleVideoUploaded(placeholderId, result.attachment, result.video);
      },
      error: (err) => {
        pending.state$.next({ uploading: false });
        this.failPendingVideo(placeholderId);
        this.pop.error(toError(err));
      },
    });
  }

  private async handleVideoUploaded(placeholderId: string, attachment: Attachment, video: _Video): Promise<void> {
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending) return;
    pending.id = video.id;
    pending.attachmentId = attachment.id;
    pending.placeholder.uploading = false;
    this.broadcastVideoUploadEnd(placeholderId);

    const targetEvent = this.events.find(e => e.id === pending.eventId);
    if (targetEvent) {
      if (!targetEvent.videos) targetEvent.videos = [];
      if (!targetEvent.videos.find(v => v.id === video.id)) targetEvent.videos.push(video);
    }
    this.removeVideoPlaceholder(placeholderId);
    if (!pending.dialogOpen) {
      URL.revokeObjectURL(pending.url);
      this.pendingVideos.delete(placeholderId);
    }
    if (pending.recording) {
      const attachments: string[] = targetEvent?.message?.["attachments"] ?? [];
      if (targetEvent && !attachments.includes(attachment.id)) targetEvent.message["attachments"] = [...attachments, attachment.id];
    } else {
      await this.updateStreamEvent(attachment);
    }
    if (pending.segment) this.ws.streamEvent("attachment", { id: attachment.id });
  }

  private failPendingVideo(placeholderId: string): void {
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending?.segment) {
      this.discardPendingVideo(placeholderId);
      return;
    }
    this.broadcastVideoUploadEnd(placeholderId);
    pending.placeholder.uploading = false;
    this.pendingVideos.delete(placeholderId);
  }

  private discardPendingVideo(placeholderId: string): void {
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending) return;
    this.broadcastVideoUploadEnd(placeholderId);
    pending.placeholder.uploading = false;
    this.removeVideoPlaceholder(placeholderId);
    if (!pending.dialogOpen) URL.revokeObjectURL(pending.url);
    this.pendingVideos.delete(placeholderId);
  }

  private removeVideoPlaceholder(placeholderId: string): void {
    const pending = this.pendingVideos.get(placeholderId);
    const targetEvent = this.events.find(e => e.id === pending?.eventId);
    if (targetEvent) {
      targetEvent.captures = this.buildCaptures({
        ...targetEvent,
        captures: (targetEvent.captures ?? []).filter(c => c.id !== placeholderId),
      });
      if (this.event?.id === targetEvent.id) this.captures = targetEvent.captures;
      this.eventsData.data = [...this.events];
    } else {
      this.captures = this.captures.filter(c => c.id !== placeholderId);
    }
  }

  private seekVideoTo(video: HTMLVideoElement, time: number): Promise<void> {
    return new Promise(res => {
      video.onseeked = () => res();
      video.currentTime = time;
    });
  }

  openVideoCapture(placeholderId: string): void {
    const pending = this.pendingVideos.get(placeholderId);
    if (!pending || pending.recording) return;
    pending.dialogOpen = true;
    const targetEvent = this.events.find(e => e.id === pending.eventId);
    this.videoCaptureDialogRef = this.dialog.open(VideoCaptureComponent, {
      width: this.isMobile ? '100%' : '80%',
      height: this.isMobile ? '100%' : '80%',
      data: {
        videoUrl: pending.url,
        createdDate: pending.createdDate,
        file: pending.file,
        thumbnail: pending.thumbnail,
        event: pending.eventId,
        camera: 'carried' as const,
        uploadState$: pending.state$.asObservable(),
        onCaptureMany: (frames: { base64: string; createdDate: string }[], _isAuto?: boolean) => this.postManyCaptures(frames, pending.id!, pending.attachmentId!, targetEvent, _isAuto),
        onUploaded: (attachment: Attachment, video: _Video) => this.handleVideoUploaded(placeholderId, attachment, video),
      },
    });
    this.videoCaptureDialogRef.afterClosed().subscribe(() => {
      this.videoCaptureDialogRef = undefined;
      pending.dialogOpen = false;
      if (pending.id || !this.pendingVideos.has(placeholderId)) {
        URL.revokeObjectURL(pending.url);
        this.pendingVideos.delete(placeholderId);
      }
    });
  }

  openSavedVideo(item: Capture): void {
    if (item.type !== 'video' || !item.url) return;
    if (this.pendingVideos.has(item.id)) { this.openVideoCapture(item.id); return; }
    const targetEvent = item.event ? this.events.find(e => e.id === item.event) : undefined;
    // item.autoDone = false;
    this.dialog.open(VideoCaptureComponent, {
      width: this.isMobile ? '100%' : '80%',
      height: this.isMobile ? '100%' : '80%',
      data: {
        videoUrl: item.url,
        createdDate: item.createdDate,
        autoDone: item.autoDone,
        onCaptureMany: (frames: { base64: string; createdDate: string }[], isAuto?: boolean) => {
          const source = item.tracker && item.trackerName ? { id: item.tracker, name: item.trackerName } : undefined;
          this.postManyCaptures(frames, item.id, item.attachment!, targetEvent, isAuto, source);
          if (isAuto) {
            item.autoDone = true;
            this.videoService.putVideo({ id: item.id, autoDone: true }).pipe(first()).subscribe();
          }
        },
      },
    });
  }

  private buildCaptures(event: _Event): Capture[] {
    const images: Capture[] = (event.images ?? []).map(img => this.mapImageToCapture(img));
    const videos: Capture[] = (event.videos ?? []).map(v => this.mapVideoToCapture(v));
    const realIds = new Set([
      ...images.map(c => c.id),
      ...videos.map(c => c.id),
      ...(event.videos ?? []).map(v => v.uploadId).filter((id): id is string => !!id),
    ]);
    const realDates = new Set([...images.map(c => c.createdDate), ...videos.map(c => c.createdDate)]);
    const inFlight = (event.captures ?? []).filter(c =>
      (c.loading || c.uploading) && !realIds.has(c.id) && !realDates.has(c.createdDate)
    );
    // One attachment, one tile: a local segment and its uploaded copy overlap
    // briefly, and the server-backed entry is listed first so it wins.
    const seen = new Set<string>();
    const merged = [...images, ...videos, ...inFlight].filter((c) => {
      if (!c.attachment) return true;
      if (seen.has(c.attachment)) return false;
      seen.add(c.attachment);
      return true;
    });
    return this.sortCaptures(merged);
  }

  private sortCaptures(captures: Capture[]): Capture[] {
    return captures.sort((a, b) =>
      new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
    );
  }

  private withReload(src: string): string {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      return url.toString();
    } catch {
      return src;
    }
  }

  private mapVideoToCapture(video: _Video): Capture {
    return {
      id: video.id,
      type: 'video',
      url: video.videoSrc ?? video.url,
      thumbnail: video.thumbnail ?? video.src ?? video.url,
      attachment: video.attachment,
      tracker: video.tracker,
      createdDate: video.createdDate,
      event: video.event,
      name: video.name,
      autoDone: video.autoDone,
      database: video.database,
      projectName: video.projectName,
      trackerName: video.trackerName,
    };
  }

  refreshImage(imageId: string): string {
    const rand = crypto.randomUUID().slice(0, 8);
    this.imageRands.set(imageId, rand);
    for (const event of this.events) {
      if (!event.images?.some(img => img.id === imageId)) continue;
      event.captures = this.buildCaptures(event);
      if (this.event?.id === event.id) this.captures = event.captures;
    }
    this.eventsData.data = [...this.events];
    return rand;
  }

  withRand(url: string | undefined, imageId: string): string | undefined {
    const rand = this.imageRands.get(imageId);
    if (!url || !rand || url.startsWith('blob:') || url.startsWith('data:')) return url;
    try {
      const parsed = new URL(url);
      parsed.searchParams.set('rand', rand);
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private mapImageToCapture(image: _Image): Capture {
    return {
      id: image.id,
      type: image.type ?? 'photo',
      url: this.withRand(image.url, image.id)!,
      thumbnail: this.withRand(image.thumbnail, image.id),
      attachment: image.attachment,
      createdDate: image.createdDate,
      event: image.event,
      name: image.name,
      statuses: image.statuses,
      loading: image.loading,
      blob: image.blob,
      database: image.database,
      projectName: image.projectName,
      trackerName: image.trackerName,
    };
  }

  //------------------ Event Management ----------------------------------------
  updateStreamEvent(attachment?: Attachment, role?: "publisher" | "viewer", stream?: "tracker"): Promise<_Event> {
    return new Promise((resolve, reject) => {
      try {
        if (!this.event) {
          this.startDate = new Date().toISOString();
          const event: PostEvent = {
            type: "HSE",
            title: "Visite HSE",
            startDate: this.startDate,
            project: this.project.id,
            message: {
              users: role == "viewer" ? this.viewers.map((u) => u.id) : [],
              attachments: attachment ? [attachment.id] : [],
              ...(stream ? { stream } : {}),
            },
            user: role == "publisher" ? this.user.id : undefined,
          };
          this.eventService
            .addEvent(event)
            .pipe(first())
            .subscribe((event) => {
              if (!event) return;
              this.event = event;
              let user = this.users.find((u) => u.id === this.user.id);
              if (role == "publisher" && this.event)
                this.event.host = user;
              else if (role == "viewer" && user && this.event)
                this.event.invited = [user];

              this.event = this.handleEvent(this.event!);
              this.events.unshift(this.event!);
              this.eventsData.data = this.events;
              this.selectEvent(this.event);
              resolve(this.event!);
            });
        } else if (this.event && attachment) {
          this.event.message["attachments"] = [
            ...this.event.message["attachments"],
            attachment.id,
          ];
          let putEvent = { id: this.event.id, "message.attachments": this.event.message["attachments"] };
          this.eventService
            .updateEvent(putEvent)
            .pipe(first())
            .subscribe((res) => {
              console.log("res :>> ", res);
              resolve(this.event!);
            });
        } else {
          resolve(this.event);
        }
      } catch (err) {
        reject(err);
      }
    })
  }

  addAttachmentToEvent(image: _Image, event?: _Event) {
    event = event ?? this.event;
    if (!event) return;
    if (!event.images)
      event.images = [];
    const placeholderIdx = event.images.findIndex(img => !img.id && img.createdDate === image.createdDate);
    if (placeholderIdx !== -1) event.images.splice(placeholderIdx, 1);
    if (!event.images.find(img => img.id === image.id))
      event.images.push(image);
    if (!event.message["attachments"])
      event.message["attachments"] = [];
    if (!event.message["attachments"].find((a: string) => a === image.attachment))
      event.message["attachments"].push(image.attachment);

    event.captures = this.buildCaptures(event);

    if (event.id === this.event?.id) {
      this.event.images = [...(event.images ?? [])];
      this.captures = event.captures;
    } else {
      this.eventsData.data = [...this.events];
    }
  }

  attachRisksToEvents(risks?: Risk[]) {
    risks = risks ?? this.risks;

    console.log('----> risks :>>', risks);
    this.events!.forEach((event) => {
      const attachments: string[] = event.message?.["attachments"] ?? [];

      event.risks = risks.filter((risk) =>
        attachments.includes(risk.payload?.["attachment"] as string) && isRisk(risk)
      );
    });

    this.eventsData.data = [...this.events!];
  }

  selectEvent(event?: _Event) {
    this.event = event;
    if (!this.event) return;
    this.eventId = event?.id as string;
    this.ws.setEvent(event?.id);
    this.captures = this.buildCaptures(this.event);
    console.log('----> this.captures :>>', this.captures);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { event: event?.id ?? null },
      queryParamsHandling: 'merge',
    });
    this.discoverRoom();
  }

  /**
   * Display name for a host/participant, whether it's a live SocketUser (carries `name`)
   * or an API User object (carries `firstName`/`lastName`).
   */
  displayName(person?: any): string {
    if (!person) return '';
    if (person.name) return person.name;
    const full = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    return full || person.email || '';
  }

  handleEvent(event: _Event) {
    // TODO: use letters avatar
    if (event?.host && event.host.avatar?.includes("user_default.png")) event.host.avatar = "";
    if (event?.invited) event.invited.forEach(u => { if (u.avatar?.includes("user_default.png")) u.avatar = ""; });
    const startDate = new Date(event.startDate);
    const endDate = event.endDate ? new Date(event.endDate) : null;
    let duration = "";

    if (endDate) {
      const ms = differenceInMilliseconds(endDate, startDate);
      const totalSeconds = Math.floor(ms / 1000);
      const totalHours = Math.floor(totalSeconds / 3600);
      const totalDays = Math.floor(totalHours / 24);
      const totalMonths = differenceInMonths(endDate, startDate);

      if (totalMonths >= 1) {
        duration = `${totalMonths} ${this.translate.instant(totalMonths > 1 ? 'duration.months' : 'duration.month')}`;
      } else if (totalDays >= 1) {
        duration = `${totalDays} ${this.translate.instant(totalDays > 1 ? 'duration.days' : 'duration.day')}`;
      } else {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        duration = `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
    }

    return {
      ...event,
      startDateText: format(startDate, "dd/MM/yy HH:mm"),
      endDateText: endDate ? format(endDate, "dd/MM/yy HH:mm") : "",
      duration,
      captures: this.buildCaptures(event),
    };
  }

  setInvited(event?: _Event) {
    event = event ?? this.event;
    if (!event) return;
    this.allInvited = [event.host as SocketUser, ...(event.invited as SocketUser[]?? [])].filter(Boolean);
    this.invited = this.allInvited.filter((u) => !this.isPublisher(u.email) && !this.isViewer(u.email));
  }

  markEventOver() {
    if (!this.event) return;
    let putEvent = { id: this.event.id, endDate: new Date().toISOString() };
    this.event.endDate = putEvent.endDate;
    this.eventService.updateEvent(putEvent).pipe(first()).subscribe(() => {
      this.event = this.handleEvent(this.event!);
      let eventIndex = this.events.findIndex(e => e.id === this.event!.id);
      if (eventIndex >= 0) {
        this.events[eventIndex] = this.event;
        this.eventsData.data = this.events;
      }
      this.pop.success(this.translate.instant('stream.finish.over'));
    });
  }

  markAnalysisDone() {
    if (!this.event) return;

    const attachmentsWithRisk = new Set(this.risks.map((r) => r.payload?.['attachment']).filter(Boolean));
    const unlabeled = this.captures.filter((img) => img.id && img.attachment && !attachmentsWithRisk.has(img.attachment));

    const finalize = () => {
      this.event!.message["analyzed"] = true;
      const putEvent = { id: this.event!.id, "message.analyzed": true };
      this.eventService.updateEvent(putEvent).pipe(first()).subscribe(() => {
        this.event = this.handleEvent(this.event!);
        const eventIndex = this.events.findIndex(e => e.id === this.event!.id);
        if (eventIndex >= 0) {
          this.events[eventIndex] = this.event;
          this.eventsData.data = this.events;
        }
        this.ws.streamEvent('event-analyzed', { eventId: this.event!.id, analyzed: true });
        this.pop.success(this.translate.instant('stream.finish.analysis_over'));
      });
    };

    if (!unlabeled.length) {
      finalize();
      return;
    }

    const ids = unlabeled.map((img) => img.id);
    this.imageService.deleteImages({ ids }).pipe(first()).subscribe(() => {
      unlabeled.forEach((img) => {
        this.captures = this.captures.filter((i) => i.id !== img.id);
        if (this.event?.images) {
          this.event.images = this.event.images.filter((i) => i.id !== img.id);
        }
        if (this.event?.message?.['attachments'] && img.attachment) {
          this.event.message['attachments'] = this.event.message['attachments'].filter((a: string) => a !== img.attachment);
        }
      });
      finalize();
    });
  }

  deleteSession() {
    if (!this.event) return;
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '450px',
      data: {
        title: this.translate.instant('stream.delete_session.title'),
        content: this.translate.instant('stream.delete_session.content'),
        okColor: 'warn',
      },
    });
    dialogRef.afterClosed().pipe(first()).subscribe((result) => {
      if (!result?.ok) return;
      const eventId = this.event!.id;
      this.eventService.removeEvent(eventId).pipe(first()).subscribe(() => {
        this.ws.streamEvent('session-deleted', { eventId });
        this.events = this.events.filter((e) => e.id !== eventId);
        this.eventsData.data = [...this.events];
        this.event = undefined;
        this.showVisitSidebar = false;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { event: null },
          queryParamsHandling: 'merge',
        });
        this.pop.success(this.translate.instant('stream.delete_session.success'));
      });
    });
  }

  //----------------- Viewer/Publisher Management ----------------------------------
  isViewer(email?: string) {
    return !!this.viewers.find(
      (viewer) => viewer.email == (email || this.user.email),
    );
  }

  addViewer(email: string) {
    if (!this.isViewer(email)) {
      let viewer = this.users.find((user) => user.email == email);
      if (viewer) {
        this.viewers.push(viewer);
      }
    }
  }

  removeViewer(email?: string) {
    let viewerIndex = this.viewers.findIndex(
      (viewer) => viewer.email == (email || this.user.email),
    );

    if (viewerIndex >= 0) {
      this.viewers.splice(viewerIndex, 1);
    }
  }

  isCurrentUser(peer: SocketUser | string) {
    return (typeof peer == "string" ? peer : peer?.email) == this.user?.email;
  }

  isPublisher(email?: string) {
    return this.publisher?.email == (email || this.user?.email);
  }

  getViewer(email?: string) {
    return this.viewers.find(
      (viewer) => viewer.email == (email || this.user.email),
    );
  }

  hasJoined(email?: string) {
    return this.isPublisher(email) || this.isViewer(email);
  }

  get labeledImages(): Capture[] {
    const attachmentsWithRisk = new Set(this.risks.map((r) => r.payload?.["attachment"]).filter(Boolean));
    return this.captures.filter((img) => img.attachment && attachmentsWithRisk.has(img.attachment));
  }


  isStreaming(user?: SocketUser) {
    user = user ?? (this.isPublisher() ? this.publisher : this.getViewer());
    if (!user || !user.streaming) return false;
    let remoteVideo = this.getVideo(user.email);
    if (!remoteVideo) return false;

    return this.isVideoPlaying(remoteVideo);
  }

  trackByViewerId(_: number, viewer: SocketUser) {
    return viewer.id;
  }

  trackById(_: number, item: Capture) {
    return item.id;
  }

  //----------------- UI & Display ---------------------------------------------
  openFullscreen(item: any) {
    this.fullscreenMedia = item.src;
    this.fullscreenType = item.type;
  }

  handleImageClick(item: Capture, images?: Capture[]) {
    // if (this.isMobile) {
    //   this.openFullscreen(item);
    // } else
    this.onShowImage(item, images);
  }

  onTableCaptureClick(row: _Event, image: Capture): void {
    if (row.id !== this.event?.id) this.selectEvent(row);
    image.type === 'video' ? this.openSavedVideo(image) : this.handleImageClick(image, row.captures ?? []);
  }

  closeFullscreen() {
    this.fullscreenMedia = null;
    this.fullscreenType = null;
  }

  toggleGallery() {
    this.showGallery = !this.showGallery;
  }

  onShowImage(image: Capture, images?: Capture[]) {
    if (image.url && image.type === 'photo')
      this.openGallery(image.id, images);
  }

  async openGallery(imageId: string, images?: Capture[]) {
    images = images ?? this.captures;
    if (!images.length) return;
    let index = images.findIndex((img) => img.id == imageId);
    const getImageInfo = (dir: string) => {
      return new Promise((resolve, reject) => {
        // Only ever resolve to a real photo — never land on a video and hand it to the
        // labeler as one. If there's no photo in the requested direction, stay put.
        const isPhoto = (i: number) => !!images[i] && images[i]!.type !== 'video';
        if (dir == "") {
          index = images.findIndex((m) => m.id == imageId);
        } else if (dir === "next") {
          let i = index + 1;
          while (i < images.length && !isPhoto(i)) i++;
          if (i < images.length) index = i;
        } else if (dir === "prev") {
          let i = index - 1;
          while (i >= 0 && !isPhoto(i)) i--;
          if (i >= 0) index = i;
        }
        if (index < 0) index = 0;
        if (index > images.length - 1) index = images.length - 1;

        this.galleryInfo = {
          index,
          canGoLeft: images.slice(0, index).some(m => m.type !== 'video'),
          canGoRight: images.slice(index + 1).some(m => m.type !== 'video'),
        };
        resolve(this.getImageInfoData(images[index]!, getImageInfo));
      });
    };

    let size = this.isMobile ? "100%" : "80%";
    this.labelerDialogRef = this.dialog.open(LabelerComponent, {
      width: size,
      height: size,
      data: await getImageInfo(""),
    });

    this.labelerDialogRef.afterClosed().subscribe(() => {
      this.labelerDialogRef = undefined;
    });
  }

  getImageInfoData(capture: Capture, getImageInfo: Function) {
    const image = this.events.flatMap(e => e.images ?? []).find(img => img.id === capture.id) ?? (capture as any);
    const risk = this.risks.find((r) => r.payload?.['attachment'] === capture.attachment);
    return {
      ...this.galleryInfo,
      image,
      project: this.project,
      user: this.user,
      labelProjects: this.labelProjects,
      labels: this.labels,
      refreshImage: (imageId: string) => this.refreshImage(imageId),
      withRand: (url: string | undefined, imageId: string) => this.withRand(url, imageId),
      onUpdate: new EventEmitter(),
      canEdit: this.auth.isPrivileged(),
      getImageInfo,
      risk,
    };
  }
  onAspectChange(event: MatButtonToggleChange) {
    this.aspect = event.value;
    this.updateAspectRatio(event.value);
    this.ws.streamEvent("aspect", { aspect: this.aspect });
  }

  getAspectRatio() {
    // While a transport is recovering, keep the aspect frozen at its pre-gap value so the
    // (temporarily empty) video box doesn't collapse to square.
    const aspect = this.reconnectingPeers.size ? (this.aspectSnapshot ?? this.aspect) : this.aspect;
    if (aspect == "1:1") return "square";
    if (aspect == "9:16") return "square"; // "portrait";
    return "landscape";
  }

  getLayoutAspect() {
    if (!this.publisher && this.trackerBroadcaster) return "landscape";
    return this.getAspectRatio();
  }

  /** Flag/unflag the remote streams the local user consumes as (re)connecting, so their
   * video box shows the loader and hides the stale/blank frame during a transport rebuild. */
  private markRemotesConnecting(connecting: boolean) {
    if (this.publisher && !this.isCurrentUser(this.publisher.email) && this.publisher.streaming) {
      this.publisher.connecting = connecting;
    }
    this.viewers.forEach(v => {
      if (!this.isCurrentUser(v.email) && v.streaming) v.connecting = connecting;
    });
  }

  getOrientation() {
    switch (screen.orientation.type) {
      case "landscape-primary":
      case "landscape-secondary":
        return "16:9";
      case "portrait-secondary":
      case "portrait-primary":
      // return '9:16';
      default:
        return "1:1";
    }
  }

  //---------------- Utility functions ------------------------------------------
  getMicLabel(user: SocketUser) {
    let suffix = !user.muted
      ? this.isCurrentUser(user)
        ? "turn_off"
        : "on"
      : this.isCurrentUser(user)
        ? "turn_on"
        : "off";
    return this.translate.instant(`stream.mic.${suffix}`);
  }

  getCameraLabel(user: SocketUser) {
    let suffix = user.camera
      ? this.isCurrentUser(user)
        ? "turn_off"
        : "on"
      : this.isCurrentUser(user)
        ? "turn_on"
        : "off";
    return this.translate.instant(`stream.camera.${suffix}`);
  }

  getGeolocLabel(user: SocketUser) {
    let suffix = user.geoloc
      ? this.isCurrentUser(user)
        ? "turn_off"
        : "on"
      : this.isCurrentUser(user)
        ? "turn_on"
        : "off";
    return this.translate.instant(`stream.geoloc.${suffix}`);
  }

  getPlaceholder(payload: { name?: string; type: "photo" | "video"; createdDate: string; url?: string; blob?: Blob; loading?: boolean; trackerName?: string; }): Capture {
    const { name = "", type, createdDate, url = "", blob, loading = true, trackerName = this.user?.email ?? '' } = payload;
    const id = Math.random().toString(36).substring(2);
    return {
      id, type, url, createdDate, name, loading, blob,
      trackerName,
      projectName: this.project?.name ?? '',
    };
  }

  async generatePDF(event: _Event) {
    if (!event.risks) return;
    this.pdfTool.images = event.images || [];
    this.loadingReport[event.id] = true;
    let { risks, startDate, endDate } = event;
    if (!endDate)
      endDate = event.images ? event.images[event.images.length - 1]?.createdDate : addMinutes(startDate, 10).toISOString()
    await new Promise((res) => setTimeout(res));
    await this.pdfTool.exportRisksToPDF({ risks, project: this.project, startDate: startDate as string, endDate: endDate as string || "", labelProject: this.labelProject });
    this.loadingReport[event.id] = false;
  }

  openInviteDialog() {
    const dialogRef = this.dialog.open(InviteUserComponent, {
      width: this.isMobile ? "100%" : "900px",
      maxWidth: "95vw",
      data: {
        project: this.project,
        user: this.user,
        isMobile: this.isMobile,
        event: this.event,
      },
    });
  }

  updateParams() {
    let user = this.isPublisher() ? this.publisher : this.getViewer();
    if (!user) return;

    let params = [];
    let camEnabled = !!user?.camera;
    let micEnabled = !user?.muted;
    if (camEnabled) params.push("camera");
    if (micEnabled) params.push("mic");
    if (this.isPublisher()) this.publisherParams = params;
    else if (this.isViewer()) this.viewerParams = params;
  }

  log(log: any) {
    this.ws.streamEvent("log", { log });
  }

  //------------------- Room Management --------------------------------------
  discoverRoom() {
    this.ws.streamEvent("discover");
  }

  becomePublisher(event?: _Event) {
    if (this.isPublisher()) return;
    if (this.isViewer() && this.getViewer()?.streaming) this.stopLocalViewer();
    this.ws.setEvent(this.event?.id);
    this.ws.streamEvent("publisher", { params: this.publisherParams });
    this.ws.streamEvent("aspect", { aspect: this.aspect });
    this.setJoinedParam(true);
  }

  becomeViewer() {
    if (this.isViewer()) return;
    if (this.isPublisher() && this.publisher?.streaming)
      this.stopLocalPublisher();
    this.ws.setEvent(this.event?.id);
    this.ws.streamEvent("viewer", { params: this.viewerParams });
    this.setJoinedParam(true);
  }

  private setJoinedParam(joined: boolean) {
    if (this.hasLeft) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { joined: joined || null },
      queryParamsHandling: 'merge',
    });
  }

  // ---------- Sidebar « Créer une visite HSE » ---------------------------------
  /** Ouvre la sidebar de droite pour créer/rejoindre une NOUVELLE visite. */
  async createNewSession(stream?: "tracker") {
    this.event = undefined;
    this.eventId = "";
    this.ws.setEvent(undefined);
    this.captures = [];
    this.fetchedAttachmentIds.clear();
    this.publisher = undefined;
    this.viewers = [];
    this.stateEventId = undefined;
    this.applyBroadcasterState(null);
    const joining = stream === "tracker";
    if (joining) this.joiningRoom = true;
    const created = await this.updateStreamEvent(undefined, undefined, stream).catch(() => undefined);
    if (created) this.ws.streamEvent("new-room", { eventData: this.toBroadcastEvent(created) });
    if (created && joining) return this.joinAsViewer();
    this.joiningRoom = false;
    this.showVisitSidebar = true;
  }

  closeVisitSidebar() {
    this.showVisitSidebar = false;
  }

  /** Clic sur une ligne : sélectionne la visite et ouvre l'aperçu (photos + détails). */
  onRowClick(row: _Event) {
    this.selectEvent(row);
    this.showVisitSidebar = true;
  }

  // ---------- Actions par ligne (menu « ... ») ---------------------------------
  async onSidebarFileSelected(e: Event | FileList) {
    this.onFileSelected(e);
  }

  async joinAsPublisher() {
    if (!this.canJoinEvent(this.event) || this.isTrackerVisit()) return;
    if (this.event) {
      this.becomePublisher();
      this.persistSelfJoin("publisher");
    } else {
      const newEvent = await this.updateStreamEvent(undefined, "publisher");
      this.event = newEvent;
      this.becomePublisher();
    }
  }

  async joinAsViewer() {
    if (!this.canJoinEvent(this.event)) return;
    if (this.event) {
      this.becomeViewer();
      this.persistSelfJoin("viewer");
    } else {
      const newEvent = await this.updateStreamEvent(undefined, "viewer");
      this.event = newEvent;
      this.becomeViewer();
    }
  }

  private toBroadcastEvent(e: _Event): Partial<_Event> {
    return {
      id: e.id,
      type: e.type,
      title: e.title,
      startDate: e.startDate,
      endDate: e.endDate,
      project: e.project,
      tracker: e.tracker,
      user: e.user,
      host: e.host,
      invited: e.invited,
      message: e.message,
    };
  }

  private claimUploaderRole() {
    if (!this.event || this.hasJoined()) return;
    const isMember = this.event.user === this.user.id
      || (this.event.message?.["users"] ?? []).includes(this.user.id);
    if (isMember) return;
    this.persistSelfJoin(this.publisher || this.event.user ? "viewer" : "publisher");
  }

  /**
   * Persist the current user joining an EXISTING visit (without a formal invite):
   * records the id on the event (event.user for a publisher, message.users for a
   * viewer) and reflects it immediately in the frontend display fields (host /
   * invited) so the participant shows without a page refresh. Sends `noMail` so a
   * self-join doesn't trigger an invitation email.
   */
  private persistSelfJoin(role: "publisher" | "viewer") {
    if (!this.event?.id || !this.user?.id) return;
    const isPublisher = role === "publisher";
    const me: any = { ...(this.users.find((u) => u.id === this.user.id) || this.user) };
    if (me.avatar?.includes("user_default.png")) me.avatar = "";

    const alreadyIn = isPublisher
      ? this.event.user === this.user.id
      : (this.event.message?.["users"] ?? []).includes(this.user.id);

    if (isPublisher) {
      this.event.user = this.user.id;
      this.event.host = me;
    } else {
      const users: string[] = this.event.message?.["users"] ?? [];
      if (!users.includes(this.user.id)) users.push(this.user.id);
      this.event.message["users"] = users;
      if (!this.event.invited?.some((u) => u.id === this.user.id))
        this.event.invited = [...(this.event.invited ?? []), me];
    }

    this.setInvited();
    this.eventsData.data = [...this.events];
    this.ws.streamEvent("user-added", {
      addedUser: { id: me.id, name: me.name, firstName: me.firstName, lastName: me.lastName, email: me.email, avatar: me.avatar },
      role,
    });

    if (alreadyIn) return;
    this.eventService
      .updateStreamEvent({ id: this.event.id, invitedId: this.user.id, isPublisher, noMail: true })
      .pipe(first())
      .subscribe();
  }

  canJoinEvent(event?: _Event): boolean {
    if (!event) return true;
    if (event.id == this.eventId) return true;
    // Allow joining any visit that hasn't ended (participation is persisted on join).
    return !event.endDate;
  }

  /** True when the visit already has a publisher (event.user / event.host) that isn't the current user. */
  hasOtherPublisher(event?: _Event): boolean {
    if (!event) return false;
    const publisherId = event.user || (event.host as any)?.id;
    if (event.id == "6a332e6a48d3cd153b4fab8e") {
      console.log('----> publisherId :>>', publisherId);
      console.log('----> event.host :>>', event.host);
      console.log('----> this.user?.id :>>', this.user?.id);
    }
    return !!publisherId && publisherId !== this.user?.id;
  }

  invitedAsPublisher(event?: _Event): boolean {
    return !!event && event.user === this.user?.id;
  }

  invitedAsViewer(event?: _Event): boolean {
    return !!event && ((event.message?.["users"] as string[]) ?? []).includes(this.user?.id as string);
  }

  canJoinDirectly(event?: _Event): boolean {
    return this.isTrackerVisit(event) || this.invitedAsPublisher(event) || this.invitedAsViewer(event) || this.hasOtherPublisher(event);
  }

  private cameraViewFor(event: _Event | undefined, trackerId: string): CameraView {
    const saved = event?.message?.["broadcaster"];
    return this.isTrackerVisit(event) && saved?.tracker === trackerId ? "top" : "front";
  }

  isTrackerVisit(event: _Event | undefined = this.event): boolean {
    return event?.message?.["stream"] === "tracker";
  }

  joinDirect(event: _Event) {
    this.selectEvent(event);
    if (this.invitedAsPublisher(event) && !this.isTrackerVisit(event)) this.joinAsPublisher();
    else this.joinAsViewer();
  }

  leaveRoom() {
    if (this.isAutoCapturing) {
      clearInterval(this.autoCaptureInterval);
      this.isAutoCapturing = false;
    }
    this.detectionsActive = false;
    this.hasInitDetections = false;
    this.lastBroadcasterRestore = 0;
    const ownRecording = this.isViewer() && !!this.recording && this.recording.requestedBy === this.user?.id;
    if (ownRecording) {
      this.leavingRecording = true;
      this.closeSegment();
    }
    if (this.activeBroadcaster) this.releaseBroadcaster(this.activeBroadcaster);
    this.activeBroadcaster = undefined;
    this.broadcaster = null;
    this.stopConsumerWatchdog();
    this.stopStream();
    const wasJoined = this.isPublisher() || this.isViewer() || this.joiningRoom;
    if (this.isViewer()) {
      this.stopLocalViewer(true);
      if (ownRecording) {
        void this.stopOwnRecording(this.event?.id);
      } else {
        this.ws.streamEvent("viewer-off");
      }
      this.removeViewer();
    } else if (this.isPublisher()) {
      this.ws.streamEvent("publisher-off");
      this.publisher = undefined;
    }
    this.joiningRoom = false;
    if (wasJoined) this.setJoinedParam(false);
    if (this.event) {
      this.showVisitSidebar = true;
    }
  }

  private async stopOwnRecording(eventId?: string) {
    try {
      await this.recorderStopped;
      this.ws.streamEvent("recording-stop", { event: eventId });
      this.finishActiveUpload();
    } catch (err) {
      console.error("[recording] could not close the recording:", err);
    } finally {
      this.ws.streamEvent("viewer-off", { event: eventId });
      this.leavingRecording = false;
    }
  }

  loadProjectTrackers() {
    if (!this.project?.id) return;
    this.trackerService.getTrackersLight({ project: this.project.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe((trackers: Tracker[]) => {
        this.projectTrackers = [...(trackers ?? [])].sort((a, b) => a.name.localeCompare(b.name));
      });
  }

  get trackerBroadcaster(): string | undefined {
    if (this.publisher) return undefined;
    return this.broadcaster?.name ?? (this.isTrackerVisit() ? this.savedBroadcaster?.name : undefined);
  }

  isBroadcasterMode(): boolean {
    return !!this.trackerBroadcaster;
  }

  onTrackerSelected(name: string | null) {
    this.selectedTrackerName = name;
  }

  addTrackerStream() {
    const tracker = this.projectTrackers.find(t => t.name === this.selectedTrackerName);
    if (!tracker || this.publisher || !this.isViewer() || !this.isTrackerVisit()) return;
    this.ws.streamEvent("broadcaster-add", { broadcaster: { name: tracker.name, tracker: tracker.id } });
    this.saveBroadcaster({ tracker: tracker.id, name: tracker.name });
  }

  canRemoveTracker(): boolean {
    return this.isViewer() && !this.savedBroadcaster?.verified;
  }

  private markTrackerVerified() {
    const saved = this.savedBroadcaster;
    if (!saved || saved.verified || !this.isTrackerVisit() || saved.name !== this.trackerBroadcaster) return;
    this.saveBroadcaster({ ...saved, verified: true });
  }

  removeTrackerStream() {
    if ((!this.broadcaster && !this.savedBroadcaster) || !this.canRemoveTracker()) return;
    if (this.broadcaster) this.ws.streamEvent("broadcaster-remove");
    this.saveBroadcaster(null);
  }

  get savedBroadcaster(): SavedBroadcaster | null {
    return this.event?.message?.["broadcaster"] ?? null;
  }

  private saveBroadcaster(broadcaster: SavedBroadcaster | null) {
    if (!this.event?.id) return;
    this.event.message["broadcaster"] = broadcaster;
    const putEvent = { id: this.event.id, "message.broadcaster": broadcaster };
    this.eventService
      .updateEvent(putEvent)
      .pipe(first())
      .subscribe();
  }

  private prepareTrackerVisit() {
    if (!this.isTrackerVisit()) return;
    if (!this.projectTrackers.length) this.loadProjectTrackers();
    this.restoreSavedBroadcaster();
  }

  private restoreSavedBroadcaster() {
    const saved = this.savedBroadcaster;
    if (!saved || this.broadcaster || this.publisher || !this.isViewer() || !this.isTrackerVisit()) return;
    if (!this.event || this.stateEventId !== this.event.id) return;
    if (Date.now() - this.lastBroadcasterRestore < StreamComponent.BROADCASTER_RESTORE_RETRY_MS) return;
    this.lastBroadcasterRestore = Date.now();
    this.ws.streamEvent("broadcaster-add", { broadcaster: { name: saved.name, tracker: saved.tracker } });
  }

  private applyBroadcasterState(next?: StreamBroadcaster | null) {
    this.broadcaster = next ?? null;
    this.syncBroadcasterMedia();
  }

  // The session's state reaches us as soon as it is selected, before joining
  // and before the tile exists, so only consume once joined and keep retrying
  // the attach until frames play.
  private syncBroadcasterMedia() {
    const desired = this.hasJoined() ? this.trackerBroadcaster : undefined;
    if (desired === this.activeBroadcaster) {
      if (desired && !this.trackerStreamLive) setTimeout(() => this.attachTrackerStream());
      return;
    }
    if (this.activeBroadcaster) this.releaseBroadcaster(this.activeBroadcaster);
    this.activeBroadcaster = desired;
    if (!desired) return;
    this.trackerStreamLive = false;
    this.waitForTrackerStream();
    this.mediasoup.resumeVideoConsumer(desired);
    setTimeout(() => this.attachTrackerStream());
  }

  private releaseBroadcaster(name: string) {
    if (this.isRecording && this.recording?.source === "broadcaster") this.closeSegment();
    this.mediasoup.pauseVideoConsumer(name);
    if (this.trackerVideo) this.trackerVideo.nativeElement.srcObject = null;
    this.trackerStreamLive = false;
    this.clearTrackerNoStream();
  }

  private waitForTrackerStream() {
    this.clearTrackerNoStream();
    this.trackerNoStreamTimer = setTimeout(() => {
      this.trackerNoStreamTimer = undefined;
      if (!this.trackerStreamLive) this.trackerNoStream = true;
    }, StreamComponent.TRACKER_NO_STREAM_MS);
  }

  private clearTrackerNoStream() {
    if (this.trackerNoStreamTimer) clearTimeout(this.trackerNoStreamTimer);
    this.trackerNoStreamTimer = undefined;
    this.trackerNoStream = false;
  }

  private broadcasterTracker(): { id: string; name: string } | undefined {
    if (!this.broadcaster) return undefined;
    return { id: this.broadcaster.tracker, name: this.broadcaster.name };
  }

  private attachTrackerStream() {
    const video = this.trackerVideo?.nativeElement;
    const stream = this.activeBroadcaster ? this.mediasoup.getConsumerStream(this.activeBroadcaster) : undefined;
    if (!video || !stream || (this.trackerStreamLive && video.srcObject === stream)) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      video.muted = true;
    }
    video.play()
      .then(() => {
        this.trackerStreamLive = true;
        this.clearTrackerNoStream();
        this.markTrackerVerified();
        this.resumeRecording();
      })
      .catch((err) => console.warn('[tracker-stream] play failed', err));
  }

  onLeave(): boolean {
    if (!this.hasLeft) {
      this.hasLeft = true;
      this.ngOnDestroy();
    }
    return true;
  }
}
